import React, {
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
} from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Menu,
  X,
  Plus,
  MessageSquare,
  Settings,
  LogOut,
  ChevronDown,
  Send,
  User,
  Bot,
  AlertTriangle,
  ExternalLink,
  Trash2,
  Edit2,
  Check,
  Globe,
  Download,
  Shield,
  ShieldOff,
  FileText,
  Users,
  BarChart3,
  Upload,
  RefreshCw,
  Save,
  ChevronRight,
  Home,
  Phone,
  Key,
  Eye,
  EyeOff,
  Link,
  AlertCircle,
  ArrowRightCircle,
} from "lucide-react";

// Backend URL from environment variable
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ===== AUTH STORAGE HELPER =====
// Uses sessionStorage by default for security; localStorage only when "Remember Me" is checked.
const authStorage = {
  _getStore() {
    return localStorage.getItem("rememberMe") === "true"
      ? localStorage
      : sessionStorage;
  },
  get(key) {
    return sessionStorage.getItem(key) || localStorage.getItem(key);
  },
  set(key, value) {
    this._getStore().setItem(key, value);
  },
  remove(key) {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  },
  setRememberMe(remember) {
    if (remember) {
      localStorage.setItem("rememberMe", "true");
    } else {
      localStorage.removeItem("rememberMe");
    }
  },
};

// ===== TRANSLATIONS =====
const translations = {
  de: {
    // Auth
    login: "Anmelden",
    register: "Registrieren",
    email: "E-Mail",
    password: "Passwort",
    confirmPassword: "Passwort bestätigen",
    passwordMismatch: "Passwörter stimmen nicht überein",
    showPassword: "Passwort anzeigen",
    hidePassword: "Passwort verbergen",
    name: "Name",
    rememberMe: "Angemeldet bleiben",
    forgotPassword: "Passwort vergessen?",
    noAccount: "Noch kein Konto?",
    hasAccount: "Bereits ein Konto?",
    orContinueWith: "Oder fortfahren mit",
    googleLogin: "Mit Google anmelden",

    // Email Verification
    verifyEmailTitle: "E-Mail bestätigen",
    verifyEmailSuccess: "Deine E-Mail wurde erfolgreich bestätigt!",
    verifyEmailError: "Die Bestätigung ist fehlgeschlagen.",
    verifyEmailExpired:
      "Der Bestätigungslink ist abgelaufen. Bitte registriere dich erneut.",
    verifyEmailInvalid: "Ungültiger Bestätigungslink.",
    verifyingEmail: "E-Mail wird bestätigt...",
    goToLogin: "Zur Anmeldung",

    // Registration Success
    registrationSuccess: "Registrierung erfolgreich!",
    checkEmailVerification:
      "Bitte überprüfe dein E-Mail-Postfach und klicke auf den Bestätigungslink, um dein Konto zu aktivieren.",
    checkSpamFolder:
      "Falls du keine E-Mail erhalten hast, überprüfe bitte auch deinen Spam-Ordner.",
    resendVerification: "Bestätigungslink erneut senden",
    resendSuccess: "Ein neuer Bestätigungslink wurde gesendet.",

    // Password Reset
    forgotPasswordTitle: "Passwort zurücksetzen",
    forgotPasswordDescription:
      "Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen deines Passworts.",
    sendResetLink: "Link senden",
    resetLinkSent:
      "Falls ein Konto mit dieser E-Mail existiert, haben wir dir einen Link zum Zurücksetzen gesendet.",
    checkEmailReset: "Überprüfe dein E-Mail-Postfach.",
    backToLogin: "Zurück zur Anmeldung",

    // Reset Password Page
    resetPasswordTitle: "Neues Passwort erstellen",
    resetPasswordDescription: "Gib dein neues Passwort ein.",
    newPasswordLabel: "Neues Passwort",
    confirmNewPasswordLabel: "Neues Passwort bestätigen",
    resetPassword: "Passwort zurücksetzen",
    resetPasswordSuccess: "Dein Passwort wurde erfolgreich geändert!",
    resetPasswordError: "Das Zurücksetzen ist fehlgeschlagen.",
    resetPasswordExpired:
      "Der Link ist abgelaufen. Bitte fordere einen neuen an.",
    canNowLogin: "Du kannst dich jetzt mit deinem neuen Passwort anmelden.",

    // Email verification required
    emailNotVerified: "Bitte bestätige zuerst deine E-Mail-Adresse.",

    // Chat
    newChat: "Neuer Chat",
    today: "Heute",
    yesterday: "Gestern",
    lastWeek: "Letzte 7 Tage",
    older: "Älter",
    typeMessage: "Schreibe deine Nachricht...",
    send: "Senden",
    thinking: "Empowerley denkt nach...",
    selectAgent: "Agent auswählen",

    // Navigation
    chat: "Chat",
    account: "Konto",
    admin: "Admin",
    logout: "Abmelden",
    language: "Sprache",
    german: "Deutsch",
    english: "English",

    // Account
    accountSettings: "Kontoeinstellungen",
    changePassword: "Passwort ändern",
    currentPassword: "Aktuelles Passwort",
    newPassword: "Neues Passwort",
    confirmNewPassword: "Neues Passwort bestätigen",
    dataPrivacy: "Datenschutz (DSGVO)",
    exportData: "Meine Daten exportieren",
    deleteChats: "Alle Chats löschen",
    deleteAccount: "Konto löschen",
    legalNotice: "Datenschutzerklärung, Cookies, Nutzungsbedingungen",
    deleteAccountWarning:
      "Dies löscht alle Ihre Daten dauerhaft. Diese Aktion kann nicht rückgängig gemacht werden.",

    // Admin
    dashboard: "Übersicht",
    documents: "Dokumente",
    users: "Benutzer",
    agents: "Agenten",
    systemPrompt: "System-Prompt",
    uploadDocument: "Dokument hochladen",
    addExternalLink: "Externe Quelle hinzufügen",
    reindexAll: "Alles neu indizieren",
    createAgent: "Agent erstellen",
    provider: "Anbieter",
    model: "Modell",
    apiKey: "API-Schlüssel",
    apiKeyPlaceholder: "Eigener API-Schlüssel (optional, leer = Emergent Key)",
    hasCustomKey: "Eigener Schlüssel",
    isDefault: "Standard",
    isActive: "Aktiv",
    isAdmin: "Administrator",
    makeAdmin: "Zum Admin machen",
    removeAdmin: "Admin entfernen",
    userManagement: "Benutzerverwaltung",
    role: "Rolle",
    actions: "Aktionen",
    editUser: "Benutzer bearbeiten",
    deleteUser: "Benutzer löschen",
    joined: "Beigetreten",
    docName: "Anzeigename",
    docDescription: "Beschreibung",
    externalUrl: "Externe URL",
    editDocument: "Dokument bearbeiten",
    reindexErrors: "Fehler beim Indizieren",

    // Emergency
    emergencyTitle: "WICHTIG: Notfall-Ressourcen",
    emergencyText: "Wenn Sie in unmittelbarer Gefahr sind:",
    police: "Polizei",
    ambulance: "Rettungsdienst",
    womensHelpline: "Hilfetelefon bei Gewalt",
    available247: "24/7, kostenlos, anonym",

    // Misc
    disclaimer:
      "Empowerley bietet allgemeine Rechtsinformationen und ersetzt keine professionelle Rechtsberatung.",
    footerDisclaimer:
      "Haftungsausschluss: Empowerley stellt allgemeine Rechtsinformationen und Bildungsressourcen zur Verfügung. Wir bieten keine Rechtsberatung an. Für persönliche rechtliche Beratung wende dich bitte an eine lizenzierte Fachperson. Entscheidungen über Prozesskostenhilfe trifft das Gericht. KI kann Fehler machen.",
    citations: "Quellen",
    loading: "Laden...",
    save: "Speichern",
    cancel: "Abbrechen",
    delete: "Löschen",
    edit: "Bearbeiten",
    confirm: "Bestätigen",
    error: "Ein Fehler ist aufgetreten",
    success: "Erfolgreich",
    welcome: "Empowerley",
    welcomeHeadline:
      "Verstehe deine Rechte, erkunde deine Möglichkeiten und finde klare Informationen – in einem geschützten Raum.",
    welcomeExplanation:
      "Empowerley erklärt dir rechtliche Möglichkeiten in Deutschland und hilft dir, deine nächsten Schritte zu planen.",
    welcomeQualities: "Einfühlsam, vertraulich und jederzeit für dich da.",
    startChat: "Wie kann ich heute für dich da sein?",
    viewSource: "Quelle ansehen",
    downloadSource: "Herunterladen",
    closeModal: "Schließen",
    documentPreview: "Dokumentvorschau",
    noDocumentFound: "Dokument nicht gefunden",
    suggestionHeading: "Du kannst zum Beispiel fragen:",
    suggestion1: "Was kann ich tun bei nicht einvernehmlichem Sex?",
    suggestion2: "Wie läuft ein Scheidungsverfahren in Berlin ab?",
    suggestion3: "Was kann ich bei sexueller Belästigung am Arbeitsplatz tun?",
    logoTooltip: "Zur Startseite (empowerley.com)",
    copyright: "© 2026 Empowerley. Alle Rechte vorbehalten.",
    footerLegalLinks: "Datenschutzerklärung • Cookies • Nutzungsbedingungen",
    onboardingSafetyExit: "Du musst schnell raus? Nutze Safety Exit.",
  },
  en: {
    // Auth
    login: "Login",
    suggestionHeading: "You can ask, for example:",
    suggestion1: "What can I do about non-consensual sex?",
    suggestion2: "How does a divorce proceed in Berlin?",
    suggestion3: "What can I do about sexual harassment at work?",
    onboardingSafetyExit: "Need to leave quickly? Use Safety Exit.",
    copyright: "© 2026 Empowerley. All rights reserved.",
    register: "Register",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm Password",
    passwordMismatch: "Passwords do not match",
    showPassword: "Show password",
    hidePassword: "Hide password",
    name: "Name",
    rememberMe: "Remember me",
    forgotPassword: "Forgot password?",
    noAccount: "Don't have an account?",
    hasAccount: "Already have an account?",
    orContinueWith: "Or continue with",
    googleLogin: "Login with Google",

    // Email Verification
    verifyEmailTitle: "Verify Email",
    verifyEmailSuccess: "Your email has been verified successfully!",
    verifyEmailError: "Verification failed.",
    verifyEmailExpired:
      "The verification link has expired. Please register again.",
    verifyEmailInvalid: "Invalid verification link.",
    verifyingEmail: "Verifying your email...",
    goToLogin: "Go to Login",

    // Registration Success
    registrationSuccess: "Registration successful!",
    checkEmailVerification:
      "Please check your email inbox and click the verification link to activate your account.",
    checkSpamFolder:
      "If you haven't received an email, please also check your spam folder.",
    resendVerification: "Resend verification link",
    resendSuccess: "A new verification link has been sent.",

    // Password Reset
    forgotPasswordTitle: "Reset Password",
    forgotPasswordDescription:
      "Enter your email address and we'll send you a link to reset your password.",
    sendResetLink: "Send Link",
    resetLinkSent:
      "If an account exists with this email, we've sent you a reset link.",
    checkEmailReset: "Check your email inbox.",
    backToLogin: "Back to Login",

    // Reset Password Page
    resetPasswordTitle: "Create New Password",
    resetPasswordDescription: "Enter your new password.",
    newPasswordLabel: "New Password",
    confirmNewPasswordLabel: "Confirm New Password",
    resetPassword: "Reset Password",
    resetPasswordSuccess: "Your password has been changed successfully!",
    resetPasswordError: "Password reset failed.",
    resetPasswordExpired: "This link has expired. Please request a new one.",
    canNowLogin: "You can now log in with your new password.",

    // Email verification required
    emailNotVerified: "Please verify your email address first.",

    // Chat
    newChat: "New Chat",
    today: "Today",
    yesterday: "Yesterday",
    lastWeek: "Last 7 Days",
    older: "Older",
    typeMessage: "Type your message...",
    send: "Send",
    thinking: "Empowerley is thinking...",
    selectAgent: "Select Agent",

    // Navigation
    chat: "Chat",
    account: "Account",
    admin: "Admin",
    logout: "Logout",
    language: "Language",
    german: "German",
    english: "English",

    // Account
    accountSettings: "Account Settings",
    changePassword: "Change Password",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmNewPassword: "Confirm New Password",
    dataPrivacy: "Data Privacy (GDPR)",
    exportData: "Export My Data",
    deleteChats: "Delete All Chats",
    deleteAccount: "Delete Account",
    legalNotice: "Privacy Policy, Cookie Policy, Terms of Service",
    deleteAccountWarning:
      "This will permanently delete all your data. This action cannot be undone.",

    // Admin
    dashboard: "Dashboard",
    documents: "Documents",
    users: "Users",
    agents: "Agents",
    systemPrompt: "System Prompt",
    uploadDocument: "Upload Document",
    addExternalLink: "Add External Source",
    reindexAll: "Reindex All",
    createAgent: "Create Agent",
    provider: "Provider",
    model: "Model",
    apiKey: "API Key",
    apiKeyPlaceholder: "Custom API key (optional, empty = Emergent Key)",
    hasCustomKey: "Custom Key",
    isDefault: "Default",
    isActive: "Active",
    isAdmin: "Administrator",
    makeAdmin: "Make Admin",
    removeAdmin: "Remove Admin",
    userManagement: "User Management",
    totalUsers: "Total Users",
    role: "Role",
    actions: "Actions",
    editUser: "Edit User",
    deleteUser: "Delete User",
    confirmDelete: "Are you sure?",
    docName: "Display Name",
    docDescription: "Description",
    externalUrl: "External URL",
    editDocument: "Edit Document",
    reindexErrors: "Indexing Errors",

    // Emergency
    emergencyTitle: "IMPORTANT: Emergency Resources",
    emergencyText: "If you are in immediate danger:",
    police: "Police",
    ambulance: "Ambulance",
    womensHelpline: "Violence Helpline",
    available247: "24/7, free, anonymous",

    // Misc
    disclaimer:
      "Empowerley provides general legal information and does not replace professional legal advice.",
    footerDisclaimer:
      "Disclaimer: Empowerley provides general legal information and educational resources. We do not offer legal advice. For personalized legal guidance, please consult a licensed professional. Legal aid decisions are made by the court. AI can make mistakes.",
    citations: "Sources",
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    confirm: "Confirm",
    error: "An error occurred",
    success: "Success",
    welcome: "Empowerley",
    welcomeHeadline:
      "Access clear information, understand your rights, and explore your options — all in one secure space.",
    welcomeExplanation:
      "Empowerley helps you navigate legal questions in Germany and plan your next steps.",
    welcomeQualities:
      "Supportive, confidential, and available whenever you need it.",
    startChat: "How can I be there for you today?",
    suggestionHeading: "You might want to ask about:",
    suggestion1: "What can I do in case of non-consensual sexual contact?",
    suggestion2: "How does the divorce process work in Berlin?",
    suggestion3: "What can I do about sexual harassment at work?",
    viewSource: "View Source",
    downloadSource: "Download",
    closeModal: "Close",
    documentPreview: "Document Preview",
    noDocumentFound: "Document not found",
    logoTooltip: "Go to homepage (empowerley.com)",
    copyright: "© 2026 Empowerley. All rights reserved.",
    footerLegalLinks: "Privacy Policy • Cookie Policy • Terms of Service",
  },
};

// ===== SAFETY EXIT HOOK =====
const useSafetyExit = () => {
  const handleSafetyExit = () => {
    // Clear sensitive data
    authStorage.remove("token");
    authStorage.remove("user");
    sessionStorage.clear();

    // Redirect immediately and replace history
    window.location.replace("https://asana.com/resources/best-morning-routine");
  };

  return handleSafetyExit;
};
// ===== SAFETY EXIT BUTTON =====
const SafetyExitButton = ({ className = "" }) => {
  const handleSafetyExit = useSafetyExit();

  return (
    <button
      onClick={handleSafetyExit}
      className={`flex items-center px-3 py-1.5 border border-[#5A4A44] text-[#5A4A44] bg-transparent hover:bg-[#5A4A44] hover:text-white rounded-lg text-sm font-medium transition-colors shadow-sm ${className}`}
      title="Safety Exit / Notausgang"
    >
      <span>Safety Exit</span>
      <ArrowRightCircle className="w-4 h-4 md:w-5 md:h-5 ml-1.5 md:ml-2 flex-shrink-0" />
    </button>
  );
};

// ===== TYPEWRITER EFFECT =====
const useTypewriter = (text, speed = 50) => {
  const [displayText, setDisplayText] = useState("");

  useEffect(() => {
    let i = 0;
    setDisplayText(""); // Start empty

    const timer = setInterval(() => {
      i++;
      if (i <= text.length) {
        setDisplayText(text.substring(0, i));
      } else {
        clearInterval(timer);
      }
    }, speed);

    return () => clearInterval(timer);
  }, [text, speed]);

  return displayText;
};

// Component wrapper for Typewriter
const TypewriterText = ({ text, className }) => {
  const displayText = useTypewriter(text);

  return (
    <p className={className}>
      {displayText}
      <span className="animate-pulse">|</span>
    </p>
  );
};

// ===== CONTEXT =====
const AuthContext = createContext(null);
const LanguageContext = createContext(null);

const useAuth = () => useContext(AuthContext);
const useLanguage = () => useContext(LanguageContext);

// ===== RESPONSE PARSER =====
// Backend LLM sometimes returns structured JSON inside a markdown code block
// within the summary field. This extracts the real structured data.
function parsechatResponse(raw) {
  let parsed = raw;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return { summary: parsed };
    }
  }
  if (parsed.data && typeof parsed.data === "object" && parsed.data.summary) {
    parsed = parsed.data;
  }
  if (
    parsed.result &&
    typeof parsed.result === "object" &&
    parsed.result.summary
  ) {
    parsed = parsed.result;
  }

  // Check if summary contains LLM echoing its structured JSON output
  if (parsed.summary && typeof parsed.summary === "string") {
    let jsonStr = null;

    // Case 1: JSON wrapped in markdown code fences (complete)
    const codeBlockMatch = parsed.summary.match(
      /```(?:json)?\s*\n?([\s\S]*?)\n?```/,
    );
    if (codeBlockMatch) {
      jsonStr = codeBlockMatch[1];
    }

    // Case 2: Code fence opened but never closed (truncated LLM response)
    if (!jsonStr) {
      const openFenceMatch = parsed.summary.match(
        /^```(?:json)?\s*\n?([\s\S]*)$/,
      );
      if (openFenceMatch) {
        jsonStr = openFenceMatch[1];
      }
    }

    // Case 3: Raw JSON object in summary (no fences)
    if (!jsonStr) {
      const trimmed = parsed.summary.trim();
      if (trimmed.startsWith("{") && trimmed.includes('"summary"')) {
        jsonStr = trimmed;
      }
    }

    if (jsonStr) {
      // First try clean JSON.parse
      try {
        const inner = JSON.parse(jsonStr);
        if (inner.summary && typeof inner.summary === "string") {
          return {
            ...inner,
            conversation_id: inner.conversation_id || parsed.conversation_id,
            using_fallback: inner.using_fallback ?? parsed.using_fallback,
          };
        }
      } catch {
        // JSON is invalid/truncated — extract fields with regex
        const extracted = extractFromBrokenJson(jsonStr);
        if (extracted.summary) {
          return {
            ...extracted,
            conversation_id: parsed.conversation_id,
            using_fallback: parsed.using_fallback,
            citations: extracted.citations || parsed.citations,
            risk: parsed.risk,
          };
        }
      }
    }
  }

  return parsed;
}

// Extract structured fields from truncated/invalid JSON via regex
function extractFromBrokenJson(text) {
  const result = {};

  // Extract "summary": "..."
  const summaryMatch = text.match(/"summary"\s*:\s*"((?:[^"\\]|\\.)*)"/);
  if (summaryMatch)
    result.summary = summaryMatch[1].replace(/\\"/g, '"').replace(/\\n/g, "\n");

  // Extract "disclaimer": "..."
  const disclaimerMatch = text.match(/"disclaimer"\s*:\s*"((?:[^"\\]|\\.)*)"/);
  if (disclaimerMatch)
    result.disclaimer = disclaimerMatch[1].replace(/\\"/g, '"');

  // Extract "steps": ["...", "..."]
  const stepsMatch = text.match(/"steps"\s*:\s*\[([\s\S]*?)(?:\]|$)/);
  if (stepsMatch) {
    const stepStrings = [];
    const stepRegex = /"((?:[^"\\]|\\.)*)"/g;
    let m;
    while ((m = stepRegex.exec(stepsMatch[1])) !== null) {
      stepStrings.push(m[1].replace(/\\"/g, '"').replace(/\\n/g, "\n"));
    }
    if (stepStrings.length > 0) result.steps = stepStrings;
  }

  // Extract "citations": [...]
  const citationsMatch = text.match(/"citations"\s*:\s*\[([\s\S]*?)(?:\]|$)/);
  if (citationsMatch) {
    try {
      result.citations = JSON.parse("[" + citationsMatch[1] + "]");
    } catch {
      result.citations = [];
    }
  }

  return result;
}

// ===== BUILD CHAT MESSAGE CONTENT =====
// Fix bold formatting at render time (applies to both new AND historical messages)
function fixBoldFormatting(content) {
  if (!content) return content;
  // Pattern 1: **Heading** followed by newline(s) then colon → **Heading:** inline
  let fixed = content.replace(/\*\*([^*\n]+)\*\*(?:\s*\n)+\s*:\s*/g, "**$1:** ");
  // Pattern 2: **Heading: body text** → **Heading:** body text (same line only)
  fixed = fixed.replace(/\*\*([^*:\n]+):[ \t]*([^*\n]+)\*\*/g, "**$1:** $2");
  // Pattern 3: **Bold text** followed by newline(s) then punctuation → join back
  fixed = fixed.replace(/\*\*([^*\n]+)\*\*(?:\s*\n)+\s*([,;.!?)\u2013\u2014\u2012-])[ \t]*/g, "**$1**$2 ");
  // Pattern 4: **Bold text** followed by newline(s) then lowercase/paren continuation
  fixed = fixed.replace(/\*\*([^*\n]+)\*\*(?:\s*\n)+\s*([a-z(])/g, "**$1** $2");
  // Safety net: any remaining colon orphaned at start of line → merge with previous line
  fixed = fixed.replace(/\n\s*:\s+/g, ": ");
  return fixed;
}

function buildAssistantContent(parsed) {
  let content = "";
  if (parsed.summary) {
    content += parsed.summary + "\n\n";
  }
  if (parsed.steps && parsed.steps.length > 0) {
    parsed.steps.forEach((step, i) => {
      // Strip leading number+period if the LLM already numbered them
      const cleanStep = step.replace(/^\d+\.\s*/, "");
      content += `${i + 1}. ${cleanStep}\n`;
    });
    content += "\n";
  }
  if (parsed.disclaimer) {
    content += `*${parsed.disclaimer}*`;
  }
  // Fallback
  if (!content.trim()) {
    if (parsed.answer) {
      content = parsed.answer;
    } else if (parsed.message && typeof parsed.message === "string") {
      content = parsed.message;
    }
  }

  // Apply bold formatting fix
  content = fixBoldFormatting(content);

  return content.trim();
}

// ===== API HELPER =====
const api = {
  async fetch(endpoint, options = {}) {
    const token = authStorage.get("token");
    const headers = {
      "X-Frontend-Key": process.env.REACT_APP_FRONTEND_KEY,
      ...(options.formData ? {} : { "Content-Type": "application/json" }),
      ...options.headers,
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const fetchOptions = {
      ...options,
      headers,
    };
    if (options.formData) {
      delete fetchOptions.formData;
    }

    const response = await fetch(`${BACKEND_URL}${endpoint}`, fetchOptions);

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: "Request failed" }));
      throw new Error(error.detail || "Request failed");
    }

    // Handle empty responses (204 No Content)
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  },

  get(endpoint) {
    return this.fetch(endpoint);
  },

  post(endpoint, data) {
    return this.fetch(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  postForm(endpoint, formData) {
    return this.fetch(endpoint, {
      method: "POST",
      body: formData,
      formData: true,
    });
  },

  put(endpoint, data) {
    return this.fetch(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  putForm(endpoint, formData) {
    return this.fetch(endpoint, {
      method: "PUT",
      body: formData,
      formData: true,
    });
  },

  delete(endpoint) {
    return this.fetch(endpoint, {
      method: "DELETE",
    });
  },
};

// ===== FOOTER DISCLAIMER COMPONENT =====
function FooterDisclaimer({ t, className = "" }) {
  return (
    <div
      className={`text-center px-4 py-3 text-[#7B645B]/70 leading-tight max-w-4xl mx-auto ${className}`}
    >
      <p className="text-[10px] sm:text-xs mb-2">{t.footerDisclaimer}</p>
      <p className="text-[10px] sm:text-xs mb-2">{t.copyright}</p>
      <p className="text-[10px] sm:text-xs">
        <a
          href="https://www.empowerley.com/legal"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:opacity-80 transition-opacity"
        >
          {t.footerLegalLinks}
        </a>
      </p>
    </div>
  );
}

// ===== DOCUMENT VIEWER MODAL =====
function DocumentViewerModal({ isOpen, onClose, source, t }) {
  const [document, setDocument] = useState(null);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && source) {
      setLoading(true);
      setError(null);

      // Fetch document info from admin docs endpoint
      api
        .get(`/admin/docs`)
        .then((docs) => {
          const found = docs.find(
            (d) => d.title === source || d.doc_id === source,
          );
          if (found) {
            setDocument(found);
            setContent("");
          } else {
            setError("Document not found");
          }
          setLoading(false);
        })
        .catch((err) => {
          setError(err.message);
          setLoading(false);
        });
    }
  }, [isOpen, source]);

  const handleDownload = () => {
    if (document?.doc_id) {
      const token = authStorage.get("token");
      window.open(
        `${BACKEND_URL}/admin/docs/${document.doc_id}/download`,
        "_blank",
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-safe-border">
          <div className="flex items-center">
            <FileText className="w-5 h-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-primary-900">
              {t.documentPreview}
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            {document && (
              <button
                onClick={handleDownload}
                className="flex items-center px-3 py-1.5 bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 text-sm font-medium"
              >
                <Download className="w-4 h-4 mr-1" />
                {t.downloadSource}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Document Info */}
        {document && (
          <div className="px-4 py-3 bg-primary-50 border-b border-safe-border">
            <p className="font-medium text-primary-900">
              {document.name || document.filename}
            </p>
            {document.description && (
              <p className="text-sm text-primary-700 mt-1">
                {document.description}
              </p>
            )}
            <p className="text-sm text-primary-600 mt-1">
              {document.file_type?.toUpperCase().replace(".", "")} •{" "}
              {document.chunk_count || 0} Abschnitte
            </p>
            {document.external_url && (
              <a
                href={document.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center mt-1"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                {document.external_url}
              </a>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <span className="ml-3 text-primary-600">{t.loading}</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">{t.noDocumentFound}</p>
              <p className="text-sm text-gray-400 mt-1">{source}</p>
            </div>
          ) : document?.external_url && !content ? (
            <div className="text-center py-12">
              <ExternalLink className="w-12 h-12 text-blue-400 mx-auto mb-3" />
              <p className="text-gray-700 mb-4">
                This is an external document reference
              </p>
              <a
                href={document.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open External Source
              </a>
            </div>
          ) : (
            <div className="prose prose-sm max-w-none text-primary-800">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-safe-border">
          <button
            onClick={onClose}
            className="w-full py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium"
          >
            {t.closeModal}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== LOGIN PAGE =====
function LoginPage() {
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState("");

  // Check if coming from email verification
  useEffect(() => {
    if (location.state?.verified) {
      setError("");
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResendSuccess(false);

    // Check password confirmation for registration
    if (!isLogin && password !== confirmPassword) {
      setError(t.passwordMismatch);
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        const data = await api.post("/auth/login", {
          email,
          password,
        });
        authStorage.setRememberMe(rememberMe);
        authStorage.set("token", data.access_token);
        // Fetch user profile
        const userResponse = await fetch(`${BACKEND_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${data.access_token}` },
        });
        const userData = await userResponse.json();
        authStorage.set("user", JSON.stringify(userData));
        navigate("/chat");
      } else {
        // Registration - sends verification code to email
        await api.post("/auth/register", {
          email,
          password,
        });
        setRegisteredEmail(email);
        setRegistrationSuccess(true);
      }
    } catch (err) {
      // Check for email verification required error
      if (err.message && err.message.toLowerCase().includes("verify")) {
        setError(t.emailNotVerified);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      await api.post("/auth/resend-verification", {
        email: registeredEmail,
      });
      setResendSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim() || verificationCode.length !== 6) return;
    setVerifyLoading(true);
    setVerifyError("");
    try {
      const data = await api.post("/auth/verify-email", {
        email: registeredEmail,
        code: verificationCode.trim(),
      });
      // Verification returns JWT - auto login
      authStorage.set("token", data.access_token);
      const userResponse = await fetch(`${BACKEND_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${data.access_token}` },
      });
      const userData = await userResponse.json();
      authStorage.set("user", JSON.stringify(userData));
      navigate("/chat");
    } catch (err) {
      setVerifyError(err.message);
    } finally {
      setVerifyLoading(false);
    }
  };

  const googleBtnRef = useRef(null);

  // Initialize Google Sign-In and render the official button (reliable popup flow)
  useEffect(() => {
    const initGoogle = () => {
      if (window.google?.accounts?.id && googleBtnRef.current) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: async (response) => {
            try {
              const data = await api.post("/auth/google", {
                id_token: response.credential,
              });
              authStorage.set("token", data.access_token);
              const userResponse = await fetch(`${BACKEND_URL}/auth/me`, {
                headers: { Authorization: `Bearer ${data.access_token}` },
              });
              const userData = await userResponse.json();
              authStorage.set("user", JSON.stringify(userData));
              navigate("/chat");
            } catch (err) {
              setError(err.message);
            }
          },
        });
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "signin_with",
          shape: "rectangular",
          width: googleBtnRef.current.offsetWidth || 400,
        });
      }
    };
    // GSI script may still be loading (async defer), so retry if not ready
    if (window.google?.accounts?.id) {
      initGoogle();
    } else {
      const interval = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(interval);
          initGoogle();
        }
      }, 200);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show registration success screen
  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-[#CFA795] flex flex-col relative font-sans">
        {/* Header */}
        <div className="w-full px-4 md:px-10 py-4 md:py-6 flex items-center justify-between gap-2 absolute top-0 left-0 z-10">
          <div className="flex items-center h-full flex-shrink-0">
            <a
              href="https://empowerley.com"
              title={t.logoTooltip}
              className="flex items-center h-8 md:h-12"
            >
              <img
                src="/assets/new-logo.png"
                alt="Empowerley"
                className="h-6 md:h-12 w-auto hover:opacity-90 transition-opacity object-contain"
              />
            </a>
          </div>
          <div className="flex items-center gap-1.5 md:gap-5">
            <button
              onClick={() => setLanguage(language === "de" ? "en" : "de")}
              className="flex items-center justify-center px-2.5 py-1 md:px-4 md:py-2 h-8 md:h-auto border border-white/40 rounded-full md:rounded-[12px] text-white text-xs md:text-[18px] font-medium bg-transparent hover:bg-white/12 hover:border-white/60 transition-all whitespace-nowrap gap-1.5 md:gap-2"
            >
              <Globe className="w-3.5 h-3.5 md:w-5 md:h-5 text-white flex-shrink-0" />
              <span className="hidden md:inline">
                {language === "de" ? "Deutsch" : "English"}
              </span>
              <span className="md:hidden">
                {language === "de" ? "DE" : "EN"}
              </span>
            </button>
            <SafetyExitButton className="!flex !items-center !justify-center !px-2.5 !py-1 md:!px-4 md:!py-2 !h-8 md:!h-auto !border !border-white/40 !rounded-full md:!rounded-[12px] !text-white !text-xs md:!text-[18px] !font-medium !bg-transparent hover:!bg-white/12 hover:!border-white/60 !shadow-none transition-all !whitespace-nowrap !gap-1.5 md:!gap-2" />
          </div>
        </div>

        {/* Success Content */}
        <div className="flex-1 flex flex-col justify-center items-center px-4 mt-16">
          <div className="w-full max-w-md bg-white rounded-[24px] shadow-[0_20px_40px_-5px_rgba(0,0,0,0.1)] p-8">
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-green-600" />
              </div>
            </div>

            <h1 className="text-2xl font-semibold text-[#3A2E2A] text-center mb-4">
              {t.registrationSuccess}
            </h1>

            <p className="text-[#7B645B] text-center mb-2">
              {t.checkEmailVerification}
            </p>

            <p className="text-sm text-[#7B645B]/80 text-center mb-6">
              {t.checkSpamFolder}
            </p>

            {/* Verification Code Input */}
            <div className="mb-4">
              <label className="block text-[18px] leading-[28px] font-medium text-[#3A2E2A] mb-2 text-center">
                {language === "de"
                  ? "Bestätigungscode eingeben"
                  : "Enter verification code"}
              </label>
              <input
                type="text"
                maxLength={6}
                value={verificationCode}
                onChange={(e) =>
                  setVerificationCode(
                    e.target.value.replace(/\D/g, "").slice(0, 6),
                  )
                }
                placeholder="000000"
                className="block w-full px-4 py-3 bg-[#FFFCFA] border border-[#D7C3B8] rounded-xl shadow-sm focus:ring-2 focus:ring-[#E67F7A]/35 focus:border-[#E67F7A] text-[#3A2E2A] text-[24px] text-center tracking-[0.5em] font-mono"
                data-testid="verification-code-input"
              />
            </div>

            {verifyError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-center mb-4">
                {verifyError}
              </div>
            )}

            <button
              onClick={handleVerifyCode}
              disabled={verifyLoading || verificationCode.length !== 6}
              className="w-full py-3 px-4 bg-[#E67F7A] text-white rounded-xl font-semibold hover:bg-[#D96E6A] transition-colors disabled:opacity-50 mb-4"
              data-testid="verify-code-btn"
            >
              {verifyLoading
                ? t.loading
                : language === "de"
                  ? "Code bestätigen"
                  : "Verify Code"}
            </button>

            {resendSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-center mb-4">
                {t.resendSuccess}
              </div>
            )}

            <button
              onClick={handleResendVerification}
              disabled={resendLoading}
              className="w-full py-3 px-4 border border-[#E67F7A] rounded-xl text-[#E67F7A] font-medium hover:bg-[#E67F7A]/5 transition-colors disabled:opacity-50 mb-4"
              data-testid="resend-verification-btn"
            >
              {resendLoading ? t.loading : t.resendVerification}
            </button>

            <button
              onClick={() => {
                setRegistrationSuccess(false);
                setIsLogin(true);
                setEmail(registeredEmail);
                setPassword("");
              }}
              className="w-full py-3 px-4 border border-[#D7C3B8] text-[#7B645B] rounded-xl font-medium hover:bg-gray-50 transition-colors"
              data-testid="go-to-login-btn"
            >
              {t.backToLogin}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#CFA795] flex flex-col relative font-sans">
      {/* Full Width Header - Portrait Mobile Optimized */}
      <div className="w-full px-4 md:px-10 py-4 md:py-6 flex items-center justify-between gap-2 absolute top-0 left-0 z-10 flex-nowrap overflow-hidden">
        {/* Left: Logo */}
        <div className="flex items-center h-full flex-shrink-0 min-w-0">
          <a
            href="https://empowerley.com"
            title={t.logoTooltip}
            className="flex items-center h-8 md:h-12"
          >
            <img
              src="/assets/new-logo.png"
              alt="Empowerley"
              className="h-6 md:h-12 w-auto hover:opacity-90 transition-opacity object-contain"
            />
          </a>
        </div>

        {/* Right: Language + Safety Exit */}
        <div className="flex items-center gap-1.5 md:gap-5 flex-nowrap shrink-0">
          {/* Language Control - Pill Style */}
          <button
            onClick={() => setLanguage(language === "de" ? "en" : "de")}
            className="flex items-center justify-center px-2.5 py-1 md:px-4 md:py-2 h-8 md:h-auto border border-white/40 rounded-full md:rounded-[12px] text-white text-xs md:text-[18px] leading-none md:leading-[28px] font-medium bg-transparent hover:bg-white/12 hover:border-white/60 transition-all whitespace-nowrap gap-1.5 md:gap-2"
          >
            <Globe className="w-3.5 h-3.5 md:w-5 md:h-5 text-white flex-shrink-0" />
            <span className="hidden md:inline">
              {language === "de" ? "Deutsch" : "English"}
            </span>
            <span className="md:hidden">{language === "de" ? "DE" : "EN"}</span>
          </button>

          {/* Safety Exit - Pill Style */}
          <SafetyExitButton className="!flex !items-center !justify-center !px-2.5 !py-1 md:!px-4 md:!py-2 !h-8 md:!h-auto !border !border-white/40 !rounded-full md:!rounded-[12px] !text-white !text-xs md:!text-[18px] !leading-none md:!leading-[28px] !font-medium !bg-transparent hover:!bg-white/12 hover:!border-white/60 !shadow-none transition-all !whitespace-nowrap !gap-1.5 md:!gap-2" />
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 mt-16 w-full">
        {/* Text block - aligned with login card width */}
        <div className="mx-auto w-full max-w-[calc(100vw-2rem)] sm:max-w-md text-center">
          {/* Headline */}
          <p className="text-xl leading-[1.35] text-white font-semibold">
            {t.welcomeHeadline}
          </p>
          {/* Explanation - 16px gap */}
          <p className="mt-4 text-base leading-relaxed text-white/90">
            {t.welcomeExplanation}
          </p>
          {/* Qualities - 10px gap */}
          <p className="mt-2.5 text-sm leading-relaxed text-white/80">
            {t.welcomeQualities}
          </p>
        </div>

        {/* Login box - 36-40px gap from qualities */}
        <div className="mt-9 sm:mt-10 mx-auto w-full max-w-[calc(100vw-2rem)] sm:max-w-md">
          <div className="bg-white py-6 px-5 sm:py-8 sm:px-10 shadow-[0_20px_40px_-5px_rgba(0,0,0,0.1)] rounded-[24px] sm:rounded-[20px] border border-[#FAF7F5]">
            <form className="space-y-4 sm:space-y-5" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-[18px] leading-[28px] font-medium text-[#3A2E2A] mb-1 sm:mb-2"
                >
                  {t.email}
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full px-4 h-12 lg:h-auto lg:py-3 bg-[#FFFCFA] border border-[#D7C3B8] rounded-xl shadow-sm focus:ring-2 focus:ring-[#E67F7A]/35 focus:border-[#E67F7A] text-[#3A2E2A] placeholder-[#7B645B]/50 text-[18px] leading-[28px]"
                  data-testid="login-email"
                />
              </div>

              <div className="mb-1">
                <label
                  htmlFor="password"
                  className="block text-[18px] leading-[28px] font-medium text-[#3A2E2A] mb-1 sm:mb-2"
                >
                  {t.password}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 block w-full px-4 pr-12 h-12 lg:h-auto lg:py-3 bg-[#FFFCFA] border border-[#D7C3B8] rounded-xl shadow-sm focus:ring-2 focus:ring-[#E67F7A]/35 focus:border-[#E67F7A] text-[#3A2E2A] text-[18px] leading-[28px]"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#7B645B] hover:text-[#3A2E2A] transition-colors"
                    title={showPassword ? t.hidePassword : t.showPassword}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password - Only for Registration */}
              {!isLogin && (
                <div className="mb-1">
                  <label
                    htmlFor="confirmPassword"
                    className="block text-[18px] leading-[28px] font-medium text-[#3A2E2A] mb-1 sm:mb-2"
                  >
                    {t.confirmPassword}
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      minLength={8}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="mt-1 block w-full px-4 pr-12 h-12 lg:h-auto lg:py-3 bg-[#FFFCFA] border border-[#D7C3B8] rounded-xl shadow-sm focus:ring-2 focus:ring-[#E67F7A]/35 focus:border-[#E67F7A] text-[#3A2E2A] text-[18px] leading-[28px]"
                      data-testid="login-confirm-password"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#7B645B] hover:text-[#3A2E2A] transition-colors"
                      title={
                        showConfirmPassword ? t.hidePassword : t.showPassword
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {isLogin && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 pt-1 pb-1">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 text-[#E67F7A] focus:ring-[#E67F7A] border-gray-300 rounded"
                    />
                    <label
                      htmlFor="remember-me"
                      className="ml-2 block text-[18px] leading-[28px] text-[#7B645B]"
                    >
                      {t.rememberMe}
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    className="text-[18px] leading-[28px] font-medium text-[#E67F7A] hover:text-[#D96E6A] text-left sm:text-right"
                    data-testid="forgot-password-link"
                  >
                    {t.forgotPassword}
                  </button>
                </div>
              )}

              {error && (
                <div
                  className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-[18px] leading-[28px]"
                  data-testid="auth-error"
                >
                  {error}
                </div>
              )}

              {!isLogin && (
                <p
                  className="text-xs text-[#7B645B] text-center"
                  data-testid="registration-disclaimer"
                >
                  {language === "de" ? (
                    <>
                      Mit deiner Registrierung bestätigst du, dass du unseren{" "}
                      <a
                        href="https://www.empowerley.com/legal#legal-disclaimer"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-[#E67F7A]"
                      >
                        Haftungsausschluss
                      </a>{" "}
                      gelesen hast und den{" "}
                      <a
                        href="https://www.empowerley.com/legal#terms-conditions"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-[#E67F7A]"
                      >
                        Allgemeinen Geschäftsbedingungen
                      </a>{" "}
                      zustimmst.
                    </>
                  ) : (
                    <>
                      By signing up, you confirm you have read our{" "}
                      <a
                        href="https://www.empowerley.com/legal#legal-disclaimer"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-[#E67F7A]"
                      >
                        disclaimer
                      </a>{" "}
                      and that you agree to the{" "}
                      <a
                        href="https://www.empowerley.com/legal#terms-conditions"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-[#E67F7A]"
                      >
                        terms and conditions
                      </a>
                      .
                    </>
                  )}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-[18px] leading-[28px] font-semibold text-white bg-[#E67F7A] hover:bg-[#D96E6A] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E67F7A] disabled:opacity-50 transition-colors"
                data-testid="auth-submit"
              >
                {loading ? t.loading : isLogin ? t.login : t.register}
              </button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#EAE0DC]"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white text-[#7B645B]">
                    {t.orContinueWith}
                  </span>
                </div>
              </div>

              <div
                ref={googleBtnRef}
                className="mt-4 w-full flex justify-center"
                data-testid="google-login"
              ></div>
            </div>

            <p className="mt-6 text-center text-[18px] leading-[28px] text-[#7B645B]">
              {isLogin ? t.noAccount : t.hasAccount}{" "}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="font-medium text-[#E67F7A] hover:text-[#D96E6A]"
              >
                {isLogin ? t.register : t.login}
              </button>
            </p>
            <div className="mt-6">
              <FooterDisclaimer t={t} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== PROTECTED ROUTE =====
function ProtectedRoute({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user ? true : null,
  );
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (location.state?.user) return;

    const checkAuth = async () => {
      try {
        const userData = await api.get("/auth/me");
        setIsAuthenticated(true);
        setUser(userData);
        authStorage.set("user", JSON.stringify(userData));
      } catch (error) {
        setIsAuthenticated(false);
        authStorage.remove("token");
        authStorage.remove("user");
        navigate("/login", { replace: true });
      }
    };

    checkAuth();
  }, [location.state?.user, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-safe-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// ===== CHAT SIDEBAR =====
function ChatSidebar({
  chats,
  currentChatId,
  onSelectChat,
  onNewChat,
  onDeleteChat,
  onEditChat,
  isOpen,
  onClose,
  t,
}) {
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");

  const groupChats = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const groups = { today: [], yesterday: [], lastWeek: [], older: [] };

    chats.forEach((chat) => {
      const chatDate = new Date(chat.updated_at);
      if (chatDate >= today) {
        groups.today.push(chat);
      } else if (chatDate >= yesterday) {
        groups.yesterday.push(chat);
      } else if (chatDate >= lastWeek) {
        groups.lastWeek.push(chat);
      } else {
        groups.older.push(chat);
      }
    });

    return groups;
  };

  const groups = groupChats();

  const handleSaveEdit = async (chatId) => {
    if (editTitle.trim()) {
      await onEditChat(chatId, editTitle);
    }
    setEditingId(null);
    setEditTitle("");
  };

  const renderChatGroup = (title, chatList) => {
    if (chatList.length === 0) return null;

    return (
      <div className="mb-4">
        <h3 className="text-xs lg:text-sm font-semibold text-white/90 uppercase tracking-wider px-3 mb-2">
          {title}
        </h3>
        {chatList.map((chat) => (
          <div
            key={chat.chat_id}
            className={`group flex items-center px-3 py-2 lg:px-4 lg:py-3 rounded-lg cursor-pointer transition-colors ${
              currentChatId === chat.chat_id
                ? "bg-white/30 text-white font-semibold shadow-sm ring-1 ring-white/20"
                : "text-white/80 hover:bg-white/10 hover:text-white"
            }`}
            onClick={() => !editingId && onSelectChat(chat.chat_id)}
            data-testid={`chat-item-${chat.chat_id}`}
          >
            <MessageSquare className="w-4 h-4 mr-3 flex-shrink-0 text-white/80" />
            {editingId === chat.chat_id ? (
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && handleSaveEdit(chat.chat_id)
                }
                onBlur={() => handleSaveEdit(chat.chat_id)}
                className="flex-1 bg-white/20 border border-white/30 rounded px-2 py-1 text-sm lg:text-base text-white focus:outline-none focus:ring-1 focus:ring-white/50"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span className="flex-1 truncate text-sm lg:text-base text-white/90 font-medium">
                {chat.title ||
                  (chat.message_count
                    ? `${chat.message_count} messages`
                    : "...")}
              </span>
            )}
            <div className="hidden group-hover:flex items-center ml-2 space-x-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingId(chat.chat_id);
                  setEditTitle(chat.title);
                }}
                className="p-1 hover:bg-white/20 rounded text-white/70 hover:text-white"
                title={t.edit}
              >
                <Edit2 className="w-3 h-3" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteChat(chat.chat_id);
                }}
                className="p-1 hover:bg-white/20 rounded text-white/70 hover:text-[#E67F7A]"
                title={t.delete}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar - Glassmorphism style */}
      <div
        className={`fixed lg:static inset-y-0 left-0 z-50 w-72 bg-[#D8B5A6] border-r border-white/20 flex flex-col transform transition-transform duration-300 lg:transform-none ${
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="p-4">
          <button
            onClick={onNewChat}
            className="w-full flex items-center justify-center px-4 py-3 bg-[#E67F7A] text-white rounded-[16px] hover:bg-[#D96E6A] transition-all font-medium shadow-sm hover:shadow-md"
            data-testid="new-chat-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t.newChat}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {renderChatGroup(t.today, groups.today)}
          {renderChatGroup(t.yesterday, groups.yesterday)}
          {renderChatGroup(t.lastWeek, groups.lastWeek)}
          {renderChatGroup(t.older, groups.older)}
        </div>
      </div>
    </>
  );
}

// ===== MESSAGE COMPONENT =====
function MessageBubble({ message, t, onViewSource }) {
  const { language } = useLanguage();
  const isUser = message.role === "user";
  const metadata = message.metadata || {};

  // Support new citation format: {abbr, locator, url}
  const citations = metadata.citations || [];
  const risk = metadata.risk || { level: "none" };
  const escalationBanner = metadata.escalation_banner;

  return (
    <div
      className={`flex ${
        isUser ? "justify-end" : "justify-start"
      } mb-6 message-appear px-2`}
    >
      <div
        className={`flex items-start max-w-[85%] xl:max-w-[72%] ${
          isUser ? "flex-row-reverse" : "flex-row"
        }`}
      >
        <div
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-sm overflow-hidden ${
            isUser ? "ml-2.5 mt-1 bg-[#E67F7A]" : "mr-3 bg-[#D8B5A6]"
          }`}
        >
          {isUser ? (
            <User className="w-4 h-4 text-white" />
          ) : (
            <img
              src="/assets/bot-avatar.png"
              alt="Empowerley Bot"
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div className="min-w-0">
          <div
            className={`rounded-[20px] shadow-sm px-4 py-2.5 xl:px-5 xl:py-3 ${
              isUser
                ? "bg-[#E67F7A] text-white rounded-tr-sm"
                : "bg-transparent border border-white/50 text-white rounded-tl-sm"
            }`}
          >
            {isUser ? (
              <p className="whitespace-pre-wrap text-[15px] xl:text-lg leading-snug font-medium text-left">
                {message.content}
              </p>
            ) : (
              <div className="chat-prose prose prose-invert prose-base xl:prose-lg max-w-none text-white prose-headings:text-white prose-strong:text-white prose-a:text-[#E67F7A] prose-p:mb-[0.5em] prose-p:leading-snug">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Custom strong renderer - inline by default, proper styling
                    strong: ({ node, children, ...props }) => (
                      <strong className="font-bold" {...props}>
                        {children}
                      </strong>
                    ),
                    // Custom list item to handle headings properly
                    li: ({ node, children, ...props }) => (
                      <li className="mb-2" {...props}>
                        {children}
                      </li>
                    ),
                  }}
                >
                  {fixBoldFormatting(message.content)}
                </ReactMarkdown>
              </div>
            )}
          </div>

          {/* Escalation Banner for high risk */}
          {!isUser && escalationBanner && risk.level === "high" && (
            <div className="mt-2 px-4 py-3 bg-red-600 text-white rounded-xl text-sm font-medium">
              {escalationBanner}
            </div>
          )}

          {/* Risk level indicator for medium */}
          {!isUser && risk.level === "medium" && (
            <div className="mt-2 px-3 py-2 bg-amber-500/20 border border-amber-400/50 text-amber-100 rounded-xl text-xs font-medium flex items-center">
              <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
              {t.disclaimer}
            </div>
          )}

          {/* Citations */}
          {!isUser && citations.length > 0 && (
            <div className="mt-2 px-2">
              <p className="text-xs font-medium text-white/80 mb-1">
                {t.citations}:
              </p>
              <div className="flex flex-wrap gap-2">
                {citations.map((citation, idx) => (
                  <div
                    key={`${citation.abbr || citation.source}-${idx}`}
                    className="inline-flex items-center bg-white/20 rounded-lg overflow-hidden border border-white/30"
                  >
                    <span className="px-2 py-1 text-xs text-white font-medium">
                      <FileText className="w-3 h-3 inline mr-1" />
                      {citation.abbr || citation.source}{" "}
                      {citation.locator || ""}
                    </span>
                    {citation.url && (
                      <a
                        href={citation.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2 py-1 bg-white/10 hover:bg-white/30 text-white text-xs border-l border-white/30 transition-colors"
                        title={t.viewSource}
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {!citation.url && (
                      <button
                        onClick={() =>
                          onViewSource &&
                          onViewSource(citation.abbr || citation.source)
                        }
                        className="px-2 py-1 bg-white/10 hover:bg-white/30 text-white text-xs border-l border-white/30 transition-colors"
                        title={t.viewSource}
                      >
                        <Eye className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fallback model indicator */}
          {!isUser && metadata.using_fallback && (
            <div className="mt-1 px-2">
              <span className="text-[10px] text-white/40 italic">
                {language === "de" ? "Fallback-Modell" : "Fallback model"}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== TYPING INDICATOR =====
function TypingIndicator({ t }) {
  return (
    <div className="flex justify-start mb-4 message-appear ml-2">
      <div className="flex items-start">
        <div className="flex-shrink-0 w-8 h-8 rounded-full shadow-sm overflow-hidden mr-3 bg-[#D8B5A6]">
          <img
            src="/assets/bot-avatar.png"
            alt="Empowerley Bot"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="px-4 py-2.5 xl:px-5 xl:py-3 bg-transparent border border-white/50 rounded-[20px] rounded-tl-sm">
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-white rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-white rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-white rounded-full typing-dot"></div>
            <span className="ml-2 text-[15px] xl:text-lg text-white font-medium">
              {t.thinking}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== SCROLL CUE COMPONENT =====
const ScrollCue = ({ visible, onClick }) => (
  <div
    className={`fixed z-20 transition-all duration-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
    style={{ bottom: "140px", left: "50%", transform: "translateX(-50%)" }}
  >
    <button
      onClick={onClick}
      className="w-11 h-11 rounded-full bg-white/50 backdrop-blur-md flex items-center justify-center hover:bg-white/70 transition-all cursor-pointer shadow-md"
      aria-label="Scroll down"
    >
      <ChevronDown className="w-6 h-6 text-[#7B645B]" />
    </button>
  </div>
);

// ===== CHAT PAGE =====
function ChatPage() {
  const { t, language, setLanguage } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [showScrollCue, setShowScrollCue] = useState(false);
  const chatContainerRef = useRef(null);

  // Check if there's more content to scroll (only when messages exist)
  const checkScrollPosition = () => {
    if (!chatContainerRef.current) return;
    // Only show scroll cue if there are messages from the assistant
    const hasMessages =
      messages &&
      messages.length > 0 &&
      messages.some((m) => m.role === "assistant");
    if (!hasMessages) {
      setShowScrollCue(false);
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    // Show cue if there's more than 150px to scroll
    setShowScrollCue(distanceFromBottom > 150);
  };

  const handleScroll = () => {
    checkScrollPosition();
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };
  const [isTyping, setIsTyping] = useState(false);
  const [viewingSource, setViewingSource] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load chats: server-side list merged with localStorage message cache
  useEffect(() => {
    const loadData = async () => {
      try {
        const token = authStorage.get("token");
        const localChats = JSON.parse(localStorage.getItem("chats") || "[]");

        if (token) {
          // Load conversation list from server
          const serverConvs = await api.get(
            "/chat/conversations?limit=50&offset=0",
          );
          // Merge: use server list as source of truth, keep local messages as cache
          const merged = (serverConvs || []).map((conv) => {
            const local = localChats.find((c) => c.chat_id === conv.id);
            return {
              chat_id: conv.id,
              title: local?.title || null, // will be loaded on first open
              messages: local?.messages || [],
              created_at: conv.created_at,
              updated_at: conv.updated_at,
              message_count: conv.message_count,
            };
          });
          localStorage.setItem("chats", JSON.stringify(merged));
          setChats(merged);
        } else {
          setChats(localChats);
        }
      } catch (err) {
        console.error("Failed to load conversations:", err);
        // Fallback to localStorage
        const localChats = JSON.parse(localStorage.getItem("chats") || "[]");
        setChats(localChats);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll behavior for new messages
  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];
    const isAssistant = lastMessage.role === "assistant";

    if (isAssistant) {
      // For assistant messages, scroll to the TOP of the new message
      // We need a small timeout to let the DOM render
      setTimeout(() => {
        const messageElements = document.querySelectorAll(".message-appear");
        if (messageElements.length > 0) {
          const lastElement = messageElements[messageElements.length - 1];
          // Manual scroll adjustment for top offset
          const container = document.querySelector(".overflow-y-auto");
          if (container) {
            const offset = 24; // 24px top padding
            const elementTop = lastElement.offsetTop;
            container.scrollTo({
              top: elementTop - offset,
              behavior: "smooth",
            });
          }
        }
      }, 100);
    } else {
      // For user messages, keep scrolling to bottom
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }

    // Check scroll position after content renders
    setTimeout(() => {
      checkScrollPosition();
    }, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const handleNewChat = () => {
    setCurrentChatId(null);
    setMessages([]);
    setSidebarOpen(false);
  };

  const handleSelectChat = async (chatId) => {
    const chat = chats.find((c) => c.chat_id === chatId);
    if (!chat) return;

    setCurrentChatId(chatId);
    setSidebarOpen(false);

    // If we already have messages cached locally, show them immediately
    if (chat.messages && chat.messages.length > 0) {
      setMessages(chat.messages);
      return;
    }

    // Otherwise load from server
    try {
      const data = await api.get(`/chat/conversations/${chatId}`);
      const serverMessages = (data.messages || []).map((m, i) => ({
        message_id: `server-${chatId}-${i}`,
        role: m.role,
        content: m.content,
        created_at: m.created_at,
        metadata:
          m.role === "assistant"
            ? {
                risk: m.risk_level
                  ? { level: m.risk_level, reasons: [] }
                  : { level: "none", reasons: [] },
              }
            : undefined,
      }));

      setMessages(serverMessages);

      // Derive title from first user message if not set
      const firstUserMsg = serverMessages.find((m) => m.role === "user");
      const title =
        chat.title ||
        (firstUserMsg
          ? firstUserMsg.content.substring(0, 50) +
            (firstUserMsg.content.length > 50 ? "..." : "")
          : data.language === "de"
            ? "Gespräch"
            : "Chat");

      // Cache to localStorage and update state
      const updatedChats = chats.map((c) =>
        c.chat_id === chatId ? { ...c, messages: serverMessages, title } : c,
      );
      localStorage.setItem("chats", JSON.stringify(updatedChats));
      setChats(updatedChats);
    } catch (err) {
      console.error("Failed to load conversation:", err);
      setMessages([]);
    }
  };

  const handleDeleteChat = async (chatId) => {
    if (!window.confirm(t.delete + "?")) return;

    try {
      // Delete from server
      await api.delete(`/chat/conversations/${chatId}`);
    } catch (err) {
      console.error("Failed to delete conversation from server:", err);
    }

    // Also remove from localStorage cache
    const savedChats = JSON.parse(localStorage.getItem("chats") || "[]");
    const updated = savedChats.filter((c) => c.chat_id !== chatId);
    localStorage.setItem("chats", JSON.stringify(updated));
    setChats(updated);
    if (currentChatId === chatId) {
      setCurrentChatId(null);
      setMessages([]);
    }
  };

  const handleEditChat = (chatId, title) => {
    const updated = chats.map((c) =>
      c.chat_id === chatId ? { ...c, title } : c,
    );
    localStorage.setItem("chats", JSON.stringify(updated));
    setChats(updated);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isTyping) return;

    const content = inputValue.trim();
    setInputValue("");

    // Reset textarea height after sending
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }

    // Use existing conversation_id or null for new conversation
    let conversationId = currentChatId;

    // Add user message optimistically
    const tempUserMsg = {
      message_id: "temp-" + Date.now(),
      role: "user",
      content,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMsg]);
    setIsTyping(true);

    try {
      const rawResponse = await api.post("/chat/query", {
        text: content,
        lang: language,
        conversation_id: conversationId,
      });

      const parsed = parsechatResponse(rawResponse);
      const newConversationId = parsed.conversation_id || `local-${Date.now()}`;
      const assistantContent = buildAssistantContent(parsed);

      const assistantMsg = {
        message_id: "assistant-" + Date.now(),
        role: "assistant",
        content: assistantContent.trim(),
        created_at: new Date().toISOString(),
        metadata: {
          citations: parsed.citations || [],
          risk: parsed.risk || { level: "none", reasons: [] },
          escalation_banner: parsed.escalation_banner || null,
          using_fallback: parsed.using_fallback || false,
        },
      };

      // Update messages
      setMessages((prev) => [
        ...prev.filter((m) => m.message_id !== tempUserMsg.message_id),
        { ...tempUserMsg, message_id: "user-" + Date.now() },
        assistantMsg,
      ]);

      // Save conversation to localStorage
      if (!currentChatId) {
        setCurrentChatId(newConversationId);
      }

      // Save chat to localStorage for sidebar
      const savedChats = JSON.parse(localStorage.getItem("chats") || "[]");
      const existingIdx = savedChats.findIndex(
        (c) => c.chat_id === newConversationId,
      );
      const updatedMessages = [
        ...messages.filter((m) => m.message_id !== tempUserMsg.message_id),
        { ...tempUserMsg, message_id: "user-" + Date.now() },
        assistantMsg,
      ];

      if (existingIdx >= 0) {
        savedChats[existingIdx].messages = updatedMessages;
        savedChats[existingIdx].updated_at = new Date().toISOString();
      } else {
        savedChats.unshift({
          chat_id: newConversationId,
          title: content.substring(0, 50) + (content.length > 50 ? "..." : ""),
          messages: updatedMessages,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
      localStorage.setItem("chats", JSON.stringify(savedChats));
      setChats(savedChats);
    } catch (err) {
      console.error("Failed to send message:", err);
      setMessages((prev) =>
        prev.filter((m) => m.message_id !== tempUserMsg.message_id),
      );
    } finally {
      setIsTyping(false);
    }
  };

  // Enter key now creates new paragraph, not send
  // Messages are sent ONLY by clicking the send button
  const handleKeyDown = (e) => {
    // Allow Enter to create new lines (default behavior)
    // Do NOT prevent default - let textarea handle multiline input naturally
  };
  const handleSuggestionClick = async (text) => {
    setInputValue(text);
    if (isTyping) return;

    const tempUserMsg = {
      message_id: "temp-" + Date.now(),
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages([...messages, tempUserMsg]);
    setIsTyping(true);
    setInputValue("");

    try {
      const rawResponse = await api.post("/chat/query", {
        text,
        lang: language,
        conversation_id: currentChatId,
      });

      const parsed = parsechatResponse(rawResponse);
      const newConversationId = parsed.conversation_id || `local-${Date.now()}`;
      const assistantContent = buildAssistantContent(parsed);

      const assistantMsg = {
        message_id: "assistant-" + Date.now(),
        role: "assistant",
        content: assistantContent.trim(),
        created_at: new Date().toISOString(),
        metadata: {
          citations: parsed.citations || [],
          risk: parsed.risk || { level: "none", reasons: [] },
          escalation_banner: parsed.escalation_banner || null,
          using_fallback: parsed.using_fallback || false,
        },
      };

      const updatedMessages = [
        { ...tempUserMsg, message_id: "user-" + Date.now() },
        assistantMsg,
      ];

      setMessages(updatedMessages);

      if (!currentChatId) {
        setCurrentChatId(newConversationId);
      }

      const savedChats = JSON.parse(localStorage.getItem("chats") || "[]");
      const existingIdx = savedChats.findIndex(
        (c) => c.chat_id === newConversationId,
      );
      if (existingIdx >= 0) {
        savedChats[existingIdx].messages = updatedMessages;
        savedChats[existingIdx].updated_at = new Date().toISOString();
      } else {
        savedChats.unshift({
          chat_id: newConversationId,
          title: text.substring(0, 50) + (text.length > 50 ? "..." : ""),
          messages: updatedMessages,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
      localStorage.setItem("chats", JSON.stringify(savedChats));
      setChats(savedChats);
    } catch (err) {
      console.error("Failed to send message:", err);
      setMessages((prev) =>
        prev.filter((m) => m.message_id !== tempUserMsg.message_id),
      );
    } finally {
      setIsTyping(false);
    }
  };

  const handleLogout = () => {
    authStorage.remove("token");
    authStorage.remove("user");
    navigate("/login");
  };

  // Input Pulse Animation Trigger
  const [showInputPulse, setShowInputPulse] = useState(false);

  // Trigger pulse after typewriter animation roughly completes (2.5s)
  useEffect(() => {
    if (messages.length === 0) {
      const timer = setTimeout(() => {
        setShowInputPulse(true);
        // Remove pulse class after animation runs to avoid persistent glow if not desired
        setTimeout(() => setShowInputPulse(false), 2000);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [messages.length]);

  // Onboarding Tooltip Logic
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    // Show onboarding only on new chat (empty messages)
    // Runs on every new chat (messages.length === 0)
    if (messages.length === 0) {
      // Clear previous timeouts if any to prevent flicker
      setShowOnboarding(false);

      // Small delay to appear after page load/language switch
      const timer = setTimeout(() => {
        setShowOnboarding(true);

        // Auto-dismiss after 6 seconds (6000ms)
        const dismissTimer = setTimeout(() => setShowOnboarding(false), 6000);
        return () => clearTimeout(dismissTimer);
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      setShowOnboarding(false);
    }
  }, [messages.length, language]);

  // Click anywhere to dismiss
  useEffect(() => {
    const handleDismiss = () => setShowOnboarding(false);
    if (showOnboarding) {
      window.addEventListener("click", handleDismiss);
      return () => window.removeEventListener("click", handleDismiss);
    }
  }, [showOnboarding]);

  return (
    <div className="h-[100dvh] flex bg-[#CFA795] p-0 md:p-4 overflow-hidden">
      <div className="flex w-full h-full bg-[#FAF7F5] rounded-none md:rounded-[24px] shadow-[0_20px_40px_-5px_rgba(0,0,0,0.08)] overflow-hidden border-0 md:border border-white/50">
        {/* Sidebar */}
        <ChatSidebar
          chats={chats}
          currentChatId={currentChatId}
          onSelectChat={handleSelectChat}
          onNewChat={handleNewChat}
          onDeleteChat={handleDeleteChat}
          onEditChat={handleEditChat}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          t={t}
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 bg-[#CFA795] relative">
          {/* Header - Portrait Mobile Optimized */}
          <header className="bg-transparent px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-2 flex-nowrap w-full">
            {/* Left Group: Menu + Logo */}
            <div className="flex items-center gap-2 min-w-0 shrink">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-0 rounded-lg flex-shrink-0 transition-colors h-8 w-8 md:h-9 md:w-9 flex items-center justify-center hover:bg-white/10"
                data-testid="sidebar-toggle"
              >
                <Menu className="w-5 h-5 text-white" />
              </button>
              <div className="flex items-center h-8 md:h-9 shrink-0">
                <img
                  src="/assets/new-logo.png"
                  alt="Empowerley"
                  className="h-6 md:h-10 w-auto object-contain"
                />
              </div>
            </div>

            {/* Right Group: Language + Exit + Profile */}
            <div className="flex items-center gap-1.5 md:gap-3 flex-shrink-0 flex-nowrap">
              {/* Language Toggle */}
              <button
                onClick={() => setLanguage(language === "de" ? "en" : "de")}
                className="flex items-center justify-center px-2 py-1 md:px-4 md:py-1.5 h-8 md:h-[36px] border border-white/40 rounded-full md:rounded-[12px] text-white text-xs md:text-[16px] leading-none md:leading-[24px] font-medium bg-transparent hover:bg-white/12 hover:border-white/60 transition-all whitespace-nowrap gap-1 md:gap-2"
              >
                <Globe className="w-3.5 h-3.5 md:w-5 md:h-5 text-white flex-shrink-0" />
                <span className="hidden md:inline">
                  {language === "de" ? "Deutsch" : "English"}
                </span>
                <span className="md:hidden">
                  {language === "de" ? "DE" : "EN"}
                </span>
              </button>

              {/* Safety Exit Button with Onboarding Cue */}
              <div className="relative">
                <SafetyExitButton className="!flex !items-center !justify-center !px-2 !py-1 md:!px-4 md:!py-1.5 !h-8 md:!h-[36px] !border !border-white/40 !rounded-full md:!rounded-[12px] !text-white !text-xs md:!text-[16px] !leading-none md:!leading-[24px] !font-medium !bg-transparent hover:!bg-white/12 hover:!border-white/60 !shadow-none transition-all !whitespace-nowrap !gap-1 md:!gap-2" />

                {/* Onboarding Tooltip */}
                <div
                  className={`absolute top-full right-0 mt-3 w-48 p-3 bg-white rounded-xl shadow-xl border border-[#FAF7F5] transform transition-all duration-500 origin-top-right z-50 ${
                    showOnboarding
                      ? "opacity-100 scale-100 translate-y-0"
                      : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                  }`}
                >
                  <div className="absolute -top-1.5 right-4 w-3 h-3 bg-white transform rotate-45 border-t border-l border-[#FAF7F5]"></div>
                  <p className="text-[#3A2E2A] text-xs font-medium leading-relaxed text-center">
                    {t.onboardingSafetyExit}
                  </p>
                </div>
              </div>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center justify-center w-8 h-8 md:w-[36px] md:h-[36px] rounded-full border border-white/40 bg-transparent hover:bg-white/12 transition-all flex-shrink-0"
                  data-testid="user-menu-btn"
                >
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full rounded-full flex items-center justify-center bg-transparent text-white">
                      <User className="w-4 h-4 md:w-5 md:h-5" />
                    </div>
                  )}
                </button>

                {userMenuOpen && (
                  <div
                    className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-50"
                    data-testid="user-dropdown"
                  >
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="font-medium text-gray-800">{user?.name}</p>
                      <p className="text-sm text-gray-500 truncate">
                        {user?.email}
                      </p>
                    </div>

                    <div className="py-2">
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          navigate("/account");
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center text-gray-700"
                        data-testid="account-link"
                      >
                        <Settings className="w-4 h-4 mr-3" />
                        {t.account}
                      </button>

                      {user?.role === "admin" && (
                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate("/admin");
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center text-gray-700"
                          data-testid="admin-link"
                        >
                          <Shield className="w-4 h-4 mr-3" />
                          {t.admin}
                        </button>
                      )}

                      <div className="border-t border-gray-100 mt-2 pt-2">
                        <button
                          onClick={handleLogout}
                          className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center text-red-600"
                          data-testid="logout-btn"
                        >
                          <LogOut className="w-4 h-4 mr-3" />
                          {t.logout}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Messages Area */}
          <div
            className="flex-1 overflow-y-auto p-4 md:p-6 relative"
            ref={chatContainerRef}
            onScroll={handleScroll}
          >
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-4">
                {/* Main Question - Moved UP */}
                <TypewriterText
                  text={t.startChat}
                  className="text-white text-3xl font-bold tracking-tight mb-8"
                />

                {/* Suggestions Section */}
                <div className="w-full max-w-2xl animate-fade-in-up">
                  <p className="text-white/90 text-lg font-medium mb-4 font-sans">
                    {t.suggestionHeading}
                  </p>
                  <div className="flex flex-col gap-3">
                    {[t.suggestion1, t.suggestion2, t.suggestion3].map(
                      (suggestion, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="w-full p-4 text-left text-white bg-transparent border border-white/60 rounded-[12px] hover:bg-white/12 transition-all duration-200 font-sans text-base leading-relaxed"
                        >
                          {suggestion}
                        </button>
                      ),
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto">
                {messages.map((message, idx) => (
                  <MessageBubble
                    key={message.message_id || idx}
                    message={message}
                    t={t}
                    onViewSource={(source) => setViewingSource(source)}
                  />
                ))}
                {isTyping && <TypingIndicator t={t} />}
                <div ref={messagesEndRef} />
              </div>
            )}
            <ScrollCue
              visible={showScrollCue && messages.length > 0}
              onClick={scrollToBottom}
            />
          </div>

          {/* Input Area */}
          <div
            className={`bg-white/80 backdrop-blur-md border-t border-[#D7C3B8]/50 transition-all duration-300 ease-in-out md:p-4 md:pb-2 ${isInputFocused ? "p-4 pb-2" : "px-4 py-2"}`}
          >
            <div className="max-w-3xl mx-auto">
              <div className="flex items-end space-x-3">
                <div
                  className={`flex-1 bg-white border border-[#D7C3B8] rounded-[20px] shadow-sm focus-within:ring-2 focus-within:ring-[#E67F7A]/30 transition-all duration-700 ${showInputPulse ? "ring-2 ring-[#E67F7A]/50 scale-[1.01] border-[#E67F7A]" : ""}`}
                >
                  <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setInputValue(newValue);
                      // Auto-resize textarea using requestAnimationFrame to prevent cursor issues
                      requestAnimationFrame(() => {
                        if (e.target) {
                          e.target.style.height = "auto";
                          const maxHeight = 120; // ~4-5 lines
                          e.target.style.height =
                            Math.min(e.target.scrollHeight, maxHeight) + "px";
                        }
                      });
                    }}
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => setIsInputFocused(false)}
                    placeholder={t.typeMessage}
                    rows={1}
                    className={`w-full bg-transparent resize-none focus:outline-none text-[#3A2E2A] placeholder-[#7B645B]/60 text-base transition-colors duration-300 md:px-4 md:py-3 overflow-y-auto ${isInputFocused ? "px-4 py-3" : "px-4 py-1"}`}
                    style={{ minHeight: "24px", maxHeight: "120px" }}
                    data-testid="message-input"
                  />
                </div>
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isTyping}
                  className={`bg-[#E67F7A] text-white rounded-[16px] hover:bg-[#D96E6A] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg active:scale-95 md:p-3 flex-shrink-0 self-center ${isInputFocused ? "p-3" : "p-2"}`}
                  data-testid="send-btn"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              {/* Disclaimer in Chat - visible on new chats, collapses after first message */}
              <div
                className={`transition-all duration-500 ease-in-out overflow-hidden ${
                  messages.some((m) => m.role === "user")
                    ? "max-h-0 opacity-0 mt-0"
                    : "max-h-40 opacity-80 mt-2"
                }`}
              >
                <FooterDisclaimer t={t} className="pb-0" />
              </div>
            </div>
          </div>
        </div>

        {/* Click outside handlers - close dropdowns when clicking outside */}
        {userMenuOpen && (
          <div
            className="fixed inset-0 z-30"
            onClick={() => {
              setUserMenuOpen(false);
            }}
            aria-hidden="true"
          />
        )}

        {/* Document Viewer Modal */}
        <DocumentViewerModal
          isOpen={!!viewingSource}
          onClose={() => setViewingSource(null)}
          source={viewingSource}
          t={t}
        />
      </div>
    </div>
  );
}

// ===== ACCOUNT PAGE =====
function AccountPage() {
  const { t, language, setLanguage } = useLanguage();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    // Check if new passwords match
    if (newPassword !== confirmNewPassword) {
      setMessage({ type: "error", text: t.passwordMismatch });
      return;
    }

    setLoading(true);

    try {
      // Use forgot-password flow: send reset code, then user resets via /reset-password page
      await api.post("/auth/forgot-password", { email: user?.email });
      setMessage({
        type: "success",
        text:
          language === "de"
            ? 'Ein Rücksetzungscode wurde an Ihre E-Mail gesendet. Bitte verwenden Sie die Seite "Passwort zurücksetzen".'
            : "A reset code has been sent to your email. Please use the Reset Password page.",
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);
      const exportData = await api.get("/auth/export");
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const date = new Date().toISOString().split("T")[0];
      a.download = `empowerley-export-${date}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChats = async () => {
    if (!window.confirm(t.deleteChats + "?")) return;

    try {
      // Delete all conversations from server
      await api.delete("/chat/conversations");
      localStorage.removeItem("chats");
      setMessage({ type: "success", text: t.success });
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm(t.deleteAccountWarning)) return;

    try {
      // Delete account from server
      await api.delete("/auth/account");
    } catch (err) {
      console.error("Failed to delete account:", err);
    }

    // Clear all local data and logout
    authStorage.remove("token");
    authStorage.remove("user");
    localStorage.removeItem("chats");
    navigate("/login");
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setUser({ ...user, language: lang });
  };

  return (
    <div className="min-h-screen bg-safe-background">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/chat")}
              className="p-2 hover:bg-gray-100 rounded-lg mr-3"
            >
              <ChevronRight className="w-5 h-5 text-gray-600 rotate-180" />
            </button>
            <h1 className="text-xl font-semibold text-gray-800">
              {t.accountSettings}
            </h1>
          </div>
          <SafetyExitButton />
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 md:p-6 space-y-6">
        {message.text && (
          <div
            className={`p-4 rounded-xl ${
              message.type === "success"
                ? "bg-green-50 text-green-700"
                : "bg-red-50 text-red-700"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Language Settings */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Globe className="w-5 h-5 mr-2 text-primary-500" />
            {t.language}
          </h2>
          <div className="flex space-x-3">
            <button
              onClick={() => handleLanguageChange("de")}
              className={`px-6 py-3 rounded-xl font-medium transition-colors ${
                language === "de"
                  ? "bg-primary-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {t.german}
            </button>
            <button
              onClick={() => handleLanguageChange("en")}
              className={`px-6 py-3 rounded-xl font-medium transition-colors ${
                language === "en"
                  ? "bg-primary-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {t.english}
            </button>
          </div>
        </div>

        {/* Change Password */}
        {user?.auth_type !== "google" && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Shield className="w-5 h-5 mr-2 text-primary-500" />
              {t.changePassword}
            </h2>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.currentPassword}
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-primary-500 focus:border-primary-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                    title={
                      showCurrentPassword ? t.hidePassword : t.showPassword
                    }
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.newPassword}
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-primary-500 focus:border-primary-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                    title={showNewPassword ? t.hidePassword : t.showPassword}
                  >
                    {showNewPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.confirmNewPassword}
                </label>
                <div className="relative">
                  <input
                    type={showConfirmNewPassword ? "text" : "password"}
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-primary-500 focus:border-primary-500"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowConfirmNewPassword(!showConfirmNewPassword)
                    }
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                    title={
                      showConfirmNewPassword ? t.hidePassword : t.showPassword
                    }
                  >
                    {showConfirmNewPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-primary-500 text-white rounded-xl hover:bg-primary-600 font-medium disabled:opacity-50"
              >
                {loading ? t.loading : t.save}
              </button>
            </form>
          </div>
        )}

        {/* GDPR Actions */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-primary-500" />
            {t.dataPrivacy}
          </h2>
          <div className="space-y-3">
            <button
              onClick={handleExportData}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-700 transition-colors"
              data-testid="export-data-btn"
            >
              <span className="flex items-center">
                <Download className="w-4 h-4 mr-3" />
                {t.exportData}
              </span>
              <ChevronRight className="w-4 h-4" />
            </button>

            <button
              onClick={handleDeleteChats}
              className="w-full flex items-center justify-between px-4 py-3 bg-amber-50 hover:bg-amber-100 rounded-xl text-amber-700 transition-colors"
              data-testid="delete-chats-btn"
            >
              <span className="flex items-center">
                <Trash2 className="w-4 h-4 mr-3" />
                {t.deleteChats}
              </span>
              <ChevronRight className="w-4 h-4" />
            </button>

            <button
              onClick={handleDeleteAccount}
              className="w-full flex items-center justify-between px-4 py-3 bg-red-50 hover:bg-red-100 rounded-xl text-red-600 transition-colors"
              data-testid="delete-account-btn"
            >
              <span className="flex items-center">
                <Trash2 className="w-4 h-4 mr-3" />
                {t.deleteAccount}
              </span>
              <ChevronRight className="w-4 h-4" />
            </button>

            <a
              href="https://www.empowerley.com/legal"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-between px-4 py-3 bg-sky-50 hover:bg-sky-100 rounded-xl text-sky-700 transition-colors"
              data-testid="legal-notice-link"
            >
              <span className="flex items-center">
                <ExternalLink className="w-4 h-4 mr-3" />
                {t.legalNotice}
              </span>
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
        <div className="pt-4 pb-8">
          <FooterDisclaimer t={t} />
        </div>
      </main>
    </div>
  );
}

// ===== ADMIN PAGE =====
function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Restrict admin access to admin role only
  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/chat");
    }
  }, [user, navigate]);

  // Don't render admin page for unauthorized users
  if (!user || user.role !== "admin") {
    return null;
  }

  return <AdminPageContent />;
}

function AdminPageContent() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [llmSettings, setLlmSettings] = useState([]);
  const [llmStatus, setLlmStatus] = useState(null);
  const [prompts, setPrompts] = useState([]);
  // Conversation detail removed for privacy
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // User editing state
  const [editingUser, setEditingUser] = useState(null);

  // Document editing state
  const [editingDocument, setEditingDocument] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    doc_id: "",
    title: "",
    language: "de",
    file: null,
  });

  // LLM Settings form state
  const [showLlmForm, setShowLlmForm] = useState(false);
  const [editingLlm, setEditingLlm] = useState(null);
  const [llmForm, setLlmForm] = useState({
    provider: "openai",
    model: "gpt-4.1-mini",
    api_key: "",
    base_url: "",
    is_active: false,
    is_fallback: false,
  });

  // Prompt form state
  const [showPromptForm, setShowPromptForm] = useState(false);
  const [promptForm, setPromptForm] = useState({
    key: "",
    language: "de",
    text: "",
    description: "",
  });

  // Check if user is admin
  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/chat");
      return;
    }
    if (user && user.role === "admin") {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const loadData = async () => {
    const results = await Promise.allSettled([
      api.get("/admin/stats"),
      api.get("/admin/users?limit=100&offset=0"),
      api.get("/admin/docs"),
      api.get("/admin/llm-settings"),
      api.get("/admin/llm-status"),
      api.get("/admin/prompts"),
    ]);

    const val = (i) =>
      results[i].status === "fulfilled" ? results[i].value : null;
    const errors = results
      .map((r, i) => (r.status === "rejected" ? r.reason?.message : null))
      .filter(Boolean);

    setStats(val(0));
    setUsers(val(1) || []);
    setDocuments(val(2) || []);
    setLlmSettings(val(3) || []);
    setLlmStatus(val(4) || null);
    setPrompts(val(5) || []);

    if (errors.length > 0) {
      console.error("Admin load errors:", errors);
      setMessage({
        type: "error",
        text: `Partially loaded. Errors: ${errors.join("; ")}`,
      });
    }
  };

  // USER MANAGEMENT FUNCTIONS
  const handleToggleAdmin = async (userId, currentRole) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    if (
      !window.confirm(
        currentRole === "admin"
          ? "Remove admin privileges from this user?"
          : "Grant admin privileges to this user?",
      )
    )
      return;

    try {
      await api.put(`/admin/users/${userId}/role`, {
        role: newRole,
      });
      setMessage({
        type: "success",
        text:
          currentRole === "admin"
            ? "Admin privileges removed"
            : "User is now admin",
      });
      loadData();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  };

  // DOCUMENT MANAGEMENT FUNCTIONS
  const handleUploadDocument = async () => {
    if (!uploadForm.file || !uploadForm.doc_id) {
      setMessage({ type: "error", text: "File and document ID are required" });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", uploadForm.file);
    formData.append("doc_id", uploadForm.doc_id);
    formData.append("title", uploadForm.title);
    formData.append("language", uploadForm.language);

    try {
      await api.postForm("/admin/docs", formData);
      setMessage({ type: "success", text: "Document uploaded" });
      setShowUploadForm(false);
      setUploadForm({ doc_id: "", title: "", language: "de", file: null });
      loadData();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm("Delete this document?")) return;

    try {
      await api.delete(`/admin/docs/${docId}`);
      setMessage({ type: "success", text: "Document deleted" });
      loadData();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  };

  const handleReindexAll = async () => {
    if (
      !window.confirm("Reindex all documents? This may take several minutes.")
    )
      return;

    setLoading(true);
    try {
      const result = await api.post("/admin/docs/reindex", {});
      setMessage({
        type: "success",
        text: `Reindex completed at ${result.finished_at}`,
      });
      loadData();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  };

  // LLM SETTINGS FUNCTIONS
  const handleSaveLlm = async () => {
    setLoading(true);
    try {
      if (editingLlm) {
        await api.put(`/admin/llm-settings/${editingLlm.id}`, {
          provider: llmForm.provider,
          model: llmForm.model,
          ...(llmForm.api_key ? { api_key: llmForm.api_key } : {}),
          base_url: llmForm.base_url || null,
          is_fallback: llmForm.is_fallback,
        });
      } else {
        await api.post("/admin/llm-settings", {
          provider: llmForm.provider,
          model: llmForm.model,
          api_key: llmForm.api_key || undefined,
          base_url: llmForm.base_url || null,
          is_active: llmForm.is_active,
          is_fallback: llmForm.is_fallback,
        });
      }
      setMessage({
        type: "success",
        text: editingLlm ? "LLM updated" : "LLM created",
      });
      setShowLlmForm(false);
      setEditingLlm(null);
      setLlmForm({
        provider: "openai",
        model: "gpt-4.1-mini",
        api_key: "",
        base_url: "",
        is_active: false,
        is_fallback: false,
      });
      loadData();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleActivateLlm = async (id) => {
    try {
      await api.post(`/admin/llm-settings/${id}/activate`, {});
      setMessage({ type: "success", text: "Provider activated" });
      loadData();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  };

  const handleDeleteLlm = async (id) => {
    if (!window.confirm("Delete this LLM provider?")) return;
    try {
      await api.delete(`/admin/llm-settings/${id}`);
      setMessage({ type: "success", text: "Provider deleted" });
      loadData();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  };

  // PROMPT FUNCTIONS
  const handleSavePrompt = async () => {
    setLoading(true);
    try {
      await api.post("/admin/prompts", promptForm);
      setMessage({ type: "success", text: "Prompt saved" });
      setShowPromptForm(false);
      setPromptForm({ key: "", language: "de", text: "", description: "" });
      loadData();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePrompt = async (key, lang) => {
    if (!window.confirm("Delete this prompt?")) return;
    try {
      await api.delete(`/admin/prompts/${key}/${lang}`);
      setMessage({ type: "success", text: "Prompt deleted" });
      loadData();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  };

  // CONVERSATION FUNCTIONS
  // Conversation viewing removed for privacy

  // RENDER
  return (
    <div className="min-h-screen bg-safe-background">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/chat")}
              className="p-2 hover:bg-gray-100 rounded-lg mr-3"
            >
              <ChevronRight className="w-5 h-5 text-gray-600 rotate-180" />
            </button>
            <h1 className="text-xl font-semibold text-gray-800">{t.admin}</h1>
          </div>
          <SafetyExitButton />
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-6">
        {message.text && (
          <div
            className={`mb-4 p-4 rounded-xl ${
              message.type === "success"
                ? "bg-green-50 text-green-700"
                : "bg-red-50 text-red-700"
            }`}
          >
            {message.text}
            <button
              onClick={() => setMessage({ type: "", text: "" })}
              className="ml-2 text-sm underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-xl mb-6 overflow-x-auto">
          {[
            "dashboard",
            "users",
            "documents",
            "llmSettings",
            "prompts",
          ].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? "bg-white text-primary-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              {tab === "llmSettings"
                ? "LLM"
                : tab === "prompts"
                  ? t.systemPrompt
                  : t[tab]}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && stats && (
          <div className="space-y-4">
            {/* LLM Status Warning */}
            {llmStatus?.using_fallback && (
              <div
                className={`p-4 rounded-xl flex items-start ${llmStatus.fallback_configured ? "bg-amber-50 border border-amber-200" : "bg-red-50 border border-red-200"}`}
              >
                <AlertTriangle
                  className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${llmStatus.fallback_configured ? "text-amber-600" : "text-red-600"}`}
                />
                <div>
                  <h3
                    className={`font-semibold ${llmStatus.fallback_configured ? "text-amber-800" : "text-red-800"}`}
                  >
                    {llmStatus.fallback_configured
                      ? language === "de"
                        ? "Fallback-Modell aktiv"
                        : "Fallback Model Active"
                      : language === "de"
                        ? "Primäres LLM nicht verfügbar!"
                        : "Primary LLM Unavailable!"}
                  </h3>
                  <p
                    className={`text-sm mt-1 ${llmStatus.fallback_configured ? "text-amber-700" : "text-red-700"}`}
                  >
                    {llmStatus.fallback_configured
                      ? language === "de"
                        ? `Primärer Anbieter (${llmStatus.primary_provider}/${llmStatus.primary_model}) ist nicht verfügbar. Fallback: ${llmStatus.fallback_model}`
                        : `Primary provider (${llmStatus.primary_provider}/${llmStatus.primary_model}) is unavailable. Using fallback: ${llmStatus.fallback_model}`
                      : language === "de"
                        ? "Kein Fallback konfiguriert — Chat ist nicht funktionsfähig! Bitte API-Guthaben aufladen oder Fallback-Anbieter einrichten."
                        : "No fallback configured — chat is broken! Please add API credits or configure a fallback provider."}
                  </p>
                  {llmStatus.fallback_reason && (
                    <p className="text-xs mt-2 text-gray-500 font-mono break-all">
                      {llmStatus.fallback_reason}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mr-4">
                    <FileText className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t.documents}</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {stats.documents}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Chunks</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {stats.chunks}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-4">
                    <BarChart3 className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Embeddings</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {stats.embeddings}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-800">
                {t.userManagement}
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-500">
                      {t.name}
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">
                      {t.email}
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">
                      {t.role}
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">
                      {t.joined}
                    </th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">
                      {t.actions}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-3 px-4 font-medium text-gray-800">
                        {u.email}
                      </td>
                      <td className="py-3 px-4 text-gray-600">{u.email}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            u.role === "admin"
                              ? "bg-primary-100 text-primary-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {u.role === "admin" ? "Admin" : "User"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleToggleAdmin(u.id, u.role)}
                            disabled={u.id === user?.id}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={
                              u.role === "admin" ? t.removeAdmin : t.makeAdmin
                            }
                          >
                            {u.role === "admin" ? (
                              <ShieldOff className="w-4 h-4" />
                            ) : (
                              <Shield className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === "documents" && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-800">
                  {t.documents}
                </h2>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowUploadForm(true)}
                    className="px-4 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 cursor-pointer font-medium flex items-center"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {t.uploadDocument}
                  </button>
                  <button
                    onClick={handleReindexAll}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 font-medium flex items-center disabled:opacity-50"
                  >
                    <RefreshCw
                      className={`w-4 h-4 mr-2 ${
                        loading ? "animate-spin" : ""
                      }`}
                    />
                    {t.reindexAll}
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-500">
                        Doc ID
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-500">
                        Title
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-500">
                        {t.language}
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-500">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-500">
                        Chunks
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-500">
                        {t.actions}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc) => (
                      <tr
                        key={doc.doc_id}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-3 px-4 font-mono text-sm text-gray-800">
                          {doc.doc_id}
                        </td>
                        <td className="py-3 px-4 font-medium text-gray-800">
                          {doc.title}
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {doc.language?.toUpperCase()}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              doc.status === "active"
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {doc.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {doc.chunks_count || "-"}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() =>
                              window.open(
                                `${BACKEND_URL}/admin/docs/${doc.doc_id}/download`,
                                "_blank",
                              )
                            }
                            className="mr-2 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                            title={t.downloadSource}
                          >
                            <Download className="w-3 h-3 inline" />
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(doc.doc_id)}
                            className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                          >
                            {t.delete}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {documents.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="py-8 text-center text-gray-500"
                        >
                          No documents uploaded yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Upload Document Modal */}
            {showUploadForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl p-6 w-full max-w-md">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    {t.uploadDocument}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Document ID *
                      </label>
                      <input
                        type="text"
                        value={uploadForm.doc_id}
                        onChange={(e) =>
                          setUploadForm({
                            ...uploadForm,
                            doc_id: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="e.g., StGB_2026"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Title
                      </label>
                      <input
                        type="text"
                        value={uploadForm.title}
                        onChange={(e) =>
                          setUploadForm({
                            ...uploadForm,
                            title: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Document title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.language}
                      </label>
                      <select
                        value={uploadForm.language}
                        onChange={(e) =>
                          setUploadForm({
                            ...uploadForm,
                            language: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="de">Deutsch</option>
                        <option value="en">English</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        File (.md preferred)
                      </label>
                      <input
                        type="file"
                        accept=".md,.txt,.pdf,.docx"
                        onChange={(e) =>
                          setUploadForm({
                            ...uploadForm,
                            file: e.target.files[0],
                          })
                        }
                        className="w-full"
                      />
                    </div>
                  </div>
                  <div className="flex space-x-3 mt-6">
                    <button
                      onClick={() => setShowUploadForm(false)}
                      className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium"
                    >
                      {t.cancel}
                    </button>
                    <button
                      onClick={handleUploadDocument}
                      disabled={loading}
                      className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 font-medium disabled:opacity-50"
                    >
                      {loading ? t.loading : t.save}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* LLM Settings Tab */}
        {activeTab === "llmSettings" && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-800">
                LLM Providers
              </h2>
              <button
                onClick={() => {
                  setEditingLlm(null);
                  setLlmForm({
                    provider: "openai",
                    model: "gpt-4.1-mini",
                    api_key: "",
                    base_url: "",
                    is_active: false,
                    is_fallback: false,
                  });
                  setShowLlmForm(true);
                }}
                className="px-4 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 font-medium flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Provider
              </button>
            </div>

            {showLlmForm && (
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <h3 className="font-semibold text-gray-800 mb-4">
                  {editingLlm ? "Edit Provider" : "Add Provider"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.provider}
                    </label>
                    <select
                      value={llmForm.provider}
                      onChange={(e) =>
                        setLlmForm({ ...llmForm, provider: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl"
                    >
                      <option value="openai">OpenAI</option>
                      <option value="anthropic">Anthropic</option>
                      <option value="google">Google</option>
                      <option value="local">Local</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.model}
                    </label>
                    <input
                      type="text"
                      value={llmForm.model}
                      onChange={(e) =>
                        setLlmForm({ ...llmForm, model: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl"
                      placeholder="e.g., gpt-4.1-mini"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Key className="w-4 h-4 inline mr-1" />
                      {t.apiKey}
                    </label>
                    <input
                      type="password"
                      value={llmForm.api_key}
                      onChange={(e) =>
                        setLlmForm({ ...llmForm, api_key: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl"
                      placeholder="sk-..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Base URL (for local)
                    </label>
                    <input
                      type="text"
                      value={llmForm.base_url}
                      onChange={(e) =>
                        setLlmForm({ ...llmForm, base_url: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl"
                      placeholder="http://localhost:8080"
                    />
                  </div>
                  {!editingLlm && (
                    <div className="flex items-center">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={llmForm.is_active}
                          onChange={(e) =>
                            setLlmForm({
                              ...llmForm,
                              is_active: e.target.checked,
                            })
                          }
                          className="mr-2"
                        />
                        {t.isActive}
                      </label>
                    </div>
                  )}
                  <div className="flex items-center">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={llmForm.is_fallback}
                        onChange={(e) =>
                          setLlmForm({
                            ...llmForm,
                            is_fallback: e.target.checked,
                          })
                        }
                        className="mr-2"
                      />
                      Fallback
                    </label>
                  </div>
                </div>
                <div className="mt-4 flex space-x-3">
                  <button
                    onClick={() => {
                      setShowLlmForm(false);
                      setEditingLlm(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300"
                  >
                    {t.cancel}
                  </button>
                  <button
                    onClick={handleSaveLlm}
                    disabled={loading || !llmForm.model}
                    className="px-4 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 disabled:opacity-50"
                  >
                    {loading ? t.loading : t.save}
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {llmSettings.map((llm) => (
                <div
                  key={llm.id}
                  className="p-4 border border-gray-200 rounded-xl hover:border-primary-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center">
                        <h3 className="font-semibold text-gray-800">
                          {llm.provider}
                        </h3>
                        {llm.is_active && (
                          <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                            {t.isActive}
                          </span>
                        )}
                        {llm.is_fallback && (
                          <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                            Fallback
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{llm.model}</p>
                      {llm.base_url && (
                        <p className="text-xs text-gray-400 mt-1">
                          {llm.base_url}
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      {!llm.is_active && (
                        <button
                          onClick={() => handleActivateLlm(llm.id)}
                          className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                        >
                          Activate
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setEditingLlm(llm);
                          setLlmForm({
                            provider: llm.provider,
                            model: llm.model,
                            api_key: "",
                            base_url: llm.base_url || "",
                            is_active: llm.is_active,
                            is_fallback: llm.is_fallback || false,
                          });
                          setShowLlmForm(true);
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <Edit2 className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => handleDeleteLlm(llm.id)}
                        className="p-2 hover:bg-red-100 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {llmSettings.length === 0 && (
                <p className="text-center text-gray-500 py-8">
                  No LLM providers configured
                </p>
              )}
            </div>
          </div>
        )}

        {/* Prompts Tab */}
        {activeTab === "prompts" && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-800">
                {t.systemPrompt}
              </h2>
              <button
                onClick={() => {
                  setPromptForm({
                    key: "",
                    language: "de",
                    text: "",
                    description: "",
                  });
                  setShowPromptForm(true);
                }}
                className="px-4 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 font-medium flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Prompt
              </button>
            </div>

            {showPromptForm && (
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Key
                    </label>
                    <input
                      type="text"
                      value={promptForm.key}
                      onChange={(e) =>
                        setPromptForm({ ...promptForm, key: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl"
                      placeholder="e.g., legal_main"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.language}
                    </label>
                    <select
                      value={promptForm.language}
                      onChange={(e) =>
                        setPromptForm({
                          ...promptForm,
                          language: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl"
                    >
                      <option value="de">Deutsch</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={promptForm.description}
                      onChange={(e) =>
                        setPromptForm({
                          ...promptForm,
                          description: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl"
                      placeholder="Optional description"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prompt Text
                  </label>
                  <textarea
                    value={promptForm.text}
                    onChange={(e) =>
                      setPromptForm({ ...promptForm, text: e.target.value })
                    }
                    rows={8}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl font-mono text-sm"
                  />
                </div>
                <div className="mt-4 flex space-x-3">
                  <button
                    onClick={() => setShowPromptForm(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300"
                  >
                    {t.cancel}
                  </button>
                  <button
                    onClick={handleSavePrompt}
                    disabled={loading || !promptForm.key || !promptForm.text}
                    className="px-4 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 disabled:opacity-50"
                  >
                    {loading ? t.loading : t.save}
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {prompts.map((prompt, idx) => (
                <div
                  key={`${prompt.key}-${prompt.language}-${idx}`}
                  className="p-4 border border-gray-200 rounded-xl"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <h3 className="font-semibold text-gray-800">
                          {prompt.key}
                        </h3>
                        <span className="ml-2 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
                          {prompt.language?.toUpperCase()}
                        </span>
                      </div>
                      {prompt.description && (
                        <p className="text-sm text-gray-500 mt-1">
                          {prompt.description}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1 truncate">
                        {prompt.text?.substring(0, 100)}...
                      </p>
                      {prompt.updated_at && (
                        <p className="text-xs text-gray-400 mt-1">
                          Updated:{" "}
                          {new Date(prompt.updated_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => {
                          setPromptForm({
                            key: prompt.key,
                            language: prompt.language,
                            text: prompt.text,
                            description: prompt.description || "",
                          });
                          setShowPromptForm(true);
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <Edit2 className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() =>
                          handleDeletePrompt(prompt.key, prompt.language)
                        }
                        className="p-2 hover:bg-red-100 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {prompts.length === 0 && (
                <p className="text-center text-gray-500 py-8">
                  No prompts configured
                </p>
              )}
            </div>
          </div>
        )}

        <div className="pt-8 pb-4">
          <FooterDisclaimer t={t} />
        </div>
      </main>
    </div>
  );
}

// ===== FORGOT PASSWORD PAGE =====
function ForgotPasswordPage() {
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#CFA795] flex flex-col relative font-sans">
      {/* Header */}
      <div className="w-full px-4 md:px-10 py-4 md:py-6 flex items-center justify-between gap-2 absolute top-0 left-0 z-10">
        <div className="flex items-center h-full flex-shrink-0">
          <a
            href="https://empowerley.com"
            title={t.logoTooltip}
            className="flex items-center h-8 md:h-12"
          >
            <img
              src="/assets/new-logo.png"
              alt="Empowerley"
              className="h-6 md:h-12 w-auto hover:opacity-90 transition-opacity object-contain"
            />
          </a>
        </div>
        <div className="flex items-center gap-1.5 md:gap-5">
          <button
            onClick={() => setLanguage(language === "de" ? "en" : "de")}
            className="flex items-center justify-center px-2.5 py-1 md:px-4 md:py-2 h-8 md:h-auto border border-white/40 rounded-full md:rounded-[12px] text-white text-xs md:text-[18px] font-medium bg-transparent hover:bg-white/12 hover:border-white/60 transition-all whitespace-nowrap gap-1.5 md:gap-2"
          >
            <Globe className="w-3.5 h-3.5 md:w-5 md:h-5 text-white flex-shrink-0" />
            <span className="hidden md:inline">
              {language === "de" ? "Deutsch" : "English"}
            </span>
            <span className="md:hidden">{language === "de" ? "DE" : "EN"}</span>
          </button>
          <SafetyExitButton className="!flex !items-center !justify-center !px-2.5 !py-1 md:!px-4 md:!py-2 !h-8 md:!h-auto !border !border-white/40 !rounded-full md:!rounded-[12px] !text-white !text-xs md:!text-[18px] !font-medium !bg-transparent hover:!bg-white/12 hover:!border-white/60 !shadow-none transition-all !whitespace-nowrap !gap-1.5 md:!gap-2" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col justify-center items-center px-4 mt-16">
        <div className="w-full max-w-md bg-white rounded-[24px] shadow-[0_20px_40px_-5px_rgba(0,0,0,0.1)] p-8">
          {sent ? (
            <>
              {/* Success State */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <h1 className="text-2xl font-semibold text-[#3A2E2A] text-center mb-4">
                {t.checkEmailReset}
              </h1>
              <p className="text-[#7B645B] text-center mb-2">
                {t.resetLinkSent}
              </p>
              <p className="text-sm text-[#7B645B]/80 text-center mb-6">
                {t.checkSpamFolder}
              </p>
              <button
                onClick={() => navigate("/login")}
                className="w-full py-3 px-4 bg-[#E67F7A] text-white rounded-xl font-semibold hover:bg-[#D96E6A] transition-colors"
                data-testid="back-to-login-btn"
              >
                {t.backToLogin}
              </button>
            </>
          ) : (
            <>
              {/* Form State */}
              <h1 className="text-2xl font-semibold text-[#3A2E2A] text-center mb-2">
                {t.forgotPasswordTitle}
              </h1>
              <p className="text-[#7B645B] text-center mb-6">
                {t.forgotPasswordDescription}
              </p>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label
                    htmlFor="email"
                    className="block text-[18px] leading-[28px] font-medium text-[#3A2E2A] mb-2"
                  >
                    {t.email}
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full px-4 py-3 bg-[#FFFCFA] border border-[#D7C3B8] rounded-xl shadow-sm focus:ring-2 focus:ring-[#E67F7A]/35 focus:border-[#E67F7A] text-[#3A2E2A] text-[18px]"
                    data-testid="forgot-password-email"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-4 bg-[#E67F7A] text-white rounded-xl font-semibold hover:bg-[#D96E6A] transition-colors disabled:opacity-50 mb-4"
                  data-testid="send-reset-link-btn"
                >
                  {loading ? t.loading : t.sendResetLink}
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="w-full py-3 px-4 border border-[#D7C3B8] text-[#7B645B] rounded-xl font-medium hover:bg-gray-50 transition-colors"
                  data-testid="back-to-login-link"
                >
                  {t.backToLogin}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== RESET PASSWORD PAGE =====
function ResetPasswordPage() {
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Pre-fill email from URL params if available
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailParam = searchParams.get("email");
    if (emailParam) setEmail(emailParam);
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError(t.passwordMismatch);
      return;
    }

    setLoading(true);

    try {
      await api.post("/auth/reset-password", {
        email,
        code,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      if (err.message.toLowerCase().includes("expired")) {
        setError(t.resetPasswordExpired);
      } else {
        setError(err.message || t.resetPasswordError);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#CFA795] flex flex-col relative font-sans">
      {/* Header */}
      <div className="w-full px-4 md:px-10 py-4 md:py-6 flex items-center justify-between gap-2 absolute top-0 left-0 z-10">
        <div className="flex items-center h-full flex-shrink-0">
          <a
            href="https://empowerley.com"
            title={t.logoTooltip}
            className="flex items-center h-8 md:h-12"
          >
            <img
              src="/assets/new-logo.png"
              alt="Empowerley"
              className="h-6 md:h-12 w-auto hover:opacity-90 transition-opacity object-contain"
            />
          </a>
        </div>
        <div className="flex items-center gap-1.5 md:gap-5">
          <button
            onClick={() => setLanguage(language === "de" ? "en" : "de")}
            className="flex items-center justify-center px-2.5 py-1 md:px-4 md:py-2 h-8 md:h-auto border border-white/40 rounded-full md:rounded-[12px] text-white text-xs md:text-[18px] font-medium bg-transparent hover:bg-white/12 hover:border-white/60 transition-all whitespace-nowrap gap-1.5 md:gap-2"
          >
            <Globe className="w-3.5 h-3.5 md:w-5 md:h-5 text-white flex-shrink-0" />
            <span className="hidden md:inline">
              {language === "de" ? "Deutsch" : "English"}
            </span>
            <span className="md:hidden">{language === "de" ? "DE" : "EN"}</span>
          </button>
          <SafetyExitButton className="!flex !items-center !justify-center !px-2.5 !py-1 md:!px-4 md:!py-2 !h-8 md:!h-auto !border !border-white/40 !rounded-full md:!rounded-[12px] !text-white !text-xs md:!text-[18px] !font-medium !bg-transparent hover:!bg-white/12 hover:!border-white/60 !shadow-none transition-all !whitespace-nowrap !gap-1.5 md:!gap-2" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col justify-center items-center px-4 mt-16">
        <div className="w-full max-w-md bg-white rounded-[24px] shadow-[0_20px_40px_-5px_rgba(0,0,0,0.1)] p-8">
          {success ? (
            <>
              {/* Success State */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <h1 className="text-2xl font-semibold text-[#3A2E2A] text-center mb-4">
                {t.resetPasswordSuccess}
              </h1>
              <p className="text-[#7B645B] text-center mb-6">{t.canNowLogin}</p>
              <button
                onClick={() => navigate("/login")}
                className="w-full py-3 px-4 bg-[#E67F7A] text-white rounded-xl font-semibold hover:bg-[#D96E6A] transition-colors"
                data-testid="go-to-login-after-reset"
              >
                {t.goToLogin}
              </button>
            </>
          ) : (
            <>
              {/* Form State */}
              <h1 className="text-2xl font-semibold text-[#3A2E2A] text-center mb-2">
                {t.resetPasswordTitle}
              </h1>
              <p className="text-[#7B645B] text-center mb-6">
                {t.resetPasswordDescription}
              </p>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label
                    htmlFor="reset-email"
                    className="block text-[18px] leading-[28px] font-medium text-[#3A2E2A] mb-2"
                  >
                    {t.email}
                  </label>
                  <input
                    id="reset-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full px-4 py-3 bg-[#FFFCFA] border border-[#D7C3B8] rounded-xl shadow-sm focus:ring-2 focus:ring-[#E67F7A]/35 focus:border-[#E67F7A] text-[#3A2E2A] text-[18px]"
                    data-testid="reset-email"
                  />
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="reset-code"
                    className="block text-[18px] leading-[28px] font-medium text-[#3A2E2A] mb-2"
                  >
                    {language === "de" ? "Bestätigungscode" : "Reset Code"}
                  </label>
                  <input
                    id="reset-code"
                    type="text"
                    required
                    maxLength={6}
                    value={code}
                    onChange={(e) =>
                      setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    className="block w-full px-4 py-3 bg-[#FFFCFA] border border-[#D7C3B8] rounded-xl shadow-sm focus:ring-2 focus:ring-[#E67F7A]/35 focus:border-[#E67F7A] text-[#3A2E2A] text-[18px] tracking-[0.3em] text-center font-mono"
                    placeholder="000000"
                    data-testid="reset-code"
                  />
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="password"
                    className="block text-[18px] leading-[28px] font-medium text-[#3A2E2A] mb-2"
                  >
                    {t.newPasswordLabel}
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full px-4 pr-12 py-3 bg-[#FFFCFA] border border-[#D7C3B8] rounded-xl shadow-sm focus:ring-2 focus:ring-[#E67F7A]/35 focus:border-[#E67F7A] text-[#3A2E2A] text-[18px]"
                      data-testid="reset-new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#7B645B] hover:text-[#3A2E2A] transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="confirmPassword"
                    className="block text-[18px] leading-[28px] font-medium text-[#3A2E2A] mb-2"
                  >
                    {t.confirmNewPasswordLabel}
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      minLength={8}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="block w-full px-4 pr-12 py-3 bg-[#FFFCFA] border border-[#D7C3B8] rounded-xl shadow-sm focus:ring-2 focus:ring-[#E67F7A]/35 focus:border-[#E67F7A] text-[#3A2E2A] text-[18px]"
                      data-testid="reset-confirm-password"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#7B645B] hover:text-[#3A2E2A] transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-4 bg-[#E67F7A] text-white rounded-xl font-semibold hover:bg-[#D96E6A] transition-colors disabled:opacity-50"
                  data-testid="reset-password-submit"
                >
                  {loading ? t.loading : t.resetPassword}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== VERIFY EMAIL PAGE =====
// Email verification is now done inline with 6-digit code on the registration page.
// This page just redirects to login.
function VerifyEmailPage() {
  return <Navigate to="/login" replace />;
}

// ===== MAIN APP =====
function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/chat" replace />} />
      <Route path="*" element={<Navigate to="/chat" replace />} />
    </Routes>
  );
}

function App() {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem("language");
    if (saved) return saved;
    const user = authStorage.get("user");
    if (user) {
      try {
        return JSON.parse(user).language || "de";
      } catch {
        return "de";
      }
    }
    return "de";
  });

  const handleSetLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem("language", lang);
  };

  const t = translations[language] || translations.de;

  return (
    <LanguageContext.Provider
      value={{ t, language, setLanguage: handleSetLanguage }}
    >
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <AppRouter />
      </BrowserRouter>
    </LanguageContext.Provider>
  );
}

export default App;
